/**
 * 건물 외피.
 *
 * 벽 자체(구멍 뚫린 매스)와 충돌은 `WALLS` 가 소유한다. 이 파일은 그 위에
 * **밖에서 보이는 것**만 얹는다 — 기단, 하부 타일 띠, 처마, 캐노피, 함석 골.
 *
 * 층을 갈라 둔다:
 *
 *   BUILDINGS (레벨: 무엇을 지을지)
 *     -> planFacades()   베이 분할 + 역할 배정. **삼각형을 안 만든다**
 *   FacadePlan (검사 가능한 중간표현)
 *     -> buildFacades()  재질 슬롯별로 합쳐서 BufferGeometry 하나씩
 *
 * 이렇게 두면 외피를 갈아 끼울 때 매스를 안 건드려도 되고, 계획만 찍어보면
 * 「어느 면에 무엇이 붙는지」를 삼각형 없이 확인할 수 있다.
 *
 * 원칙 하나: **노출된 면에만 붙인다.** 붙어 있는 두 건물 사이의 면은 아무도
 * 못 보므로 장식하지 않는다.
 */

import * as THREE from 'three';
import { onGpu } from './gpu';
import { markDecor } from './lightPool';
import { LightPools } from './decals';
import { applySnowAccum } from './outside';
import {
  BUILDINGS,
  WALL_H,
  WALL_T,
  WALLS,
  WINDOWS,
  WIN_Y,
  type Archetype,
  type BuildingDef,
  type HouseStyle,
} from '../../shared/level';

/**
 * 실치수. **지어낸 값이 아니라 실제 규격이다.**
 *
 * school — 1962~1992 학교 표준설계도. 교실 한 칸이 7.5m × 9m 로 전국이 같았다.
 *          이 건물의 리듬은 기둥 간격이 아니라 **교실 폭**이다.
 * house  — 1970년대 새마을운동 지붕개량을 거친 농가. 초가를 슬레이트나 함석으로
 *          갈아 얹은 집이고, 2010년 조사에서도 농가주택의 37.9%가 아직 슬레이트다.
 *          (너와집·굴피집은 화전민 가옥이고 1970년대에 대부분 사라져 문화재로만
 *           남았다. 2026년 마을 빈집이 너와집일 수는 없다.)
 * shed   — 함석 창고.
 */
const BAY = { school: 7.5, house: 2.4, shed: 4.0 } as const;

/** 기단. 표준설계도 학교는 «일체의 의장을 배제»했으므로 화강석 기단 같은 건 없다 */
/**
 * 기단. 표준설계도 학교는 «일체의 의장을 배제»했으므로 화강석 기단 같은 건 없다.
 *
 * 살림집을 0.28 에서 0.45 로 올렸다. **적설 지역이라 기단을 높인다** —
 * 강원 산간에서 기단이 한 뼘이면 눈이 오면 문이 안 열린다. 그리고 이 높이가
 * 계단 세 단을 만든다. 명주원이 마주친 「계단참」이 그 세 단이다.
 */
const PLINTH = { school: 0.45, house: 0.45, shed: 0.32 } as const;

/**
 * 하부 띠 — **없앴다.** 표준설계도 학교의 입면은 「일체의 의장을 배제하고
 * 채광면적 확보를 우선한 기능적 계획」이었다. 초록 타일 띠는 2000년대 이후
 * 도시 학교 리모델링에서 온 것이지 1970년대 분교에는 없다.
 */
const TILE_BAND = { school: null, house: null, shed: null } as const;

/** 문짝 반폭. 레벨의 wallRun 이 문을 뚫는 폭과 같아야 문패가 문을 안 문다 */
const DOOR_HALF = 0.95;

/** 처마 내밀기 */
const EAVE = { school: 0.55, house: 0.45, shed: 0.45 } as const;

export type Side = 'north' | 'south' | 'east' | 'west';

export interface Bay {
  side: Side;
  index: number;
  /** 베이 중심 (지면) */
  x: number;
  z: number;
  width: number;
  /** 이 베이가 현관을 물고 있나 */
  entrance: boolean;
}

export interface EdgePlan {
  side: Side;
  /** 벽이 뻗는 축 */
  along: 'x' | 'z';
  /** 고정 좌표 (남/북이면 z, 동/서면 x) */
  at: number;
  from: number;
  to: number;
  /** 밖에서 보이나. 안 보이면 아무것도 안 붙인다 */
  exposed: boolean;
  bays: Bay[];
}

export interface FacadePlan {
  id: string;
  archetype: Archetype;
  bayWidth: number;
  edges: EdgePlan[];
}

export interface FacadeDiagnostics {
  buildings: number;
  bays: number;
  hiddenEdgesSkipped: number;
  modules: Record<string, number>;
  triangles: number;
}

/** 두 건물이 맞닿아 이 면이 가려지나 */
function hidden(b: BuildingDef, side: Side): boolean {
  const eps = 0.05;
  for (const o of BUILDINGS) {
    if (o.id === b.id) continue;
    const overlapX = Math.min(b.x2, o.x2) - Math.max(b.x1, o.x1) > 1;
    const overlapZ = Math.min(b.z2, o.z2) - Math.max(b.z1, o.z1) > 1;
    if (side === 'north' && overlapX && Math.abs(o.z2 - b.z1) < eps) return true;
    if (side === 'south' && overlapX && Math.abs(o.z1 - b.z2) < eps) return true;
    if (side === 'west' && overlapZ && Math.abs(o.x2 - b.x1) < eps) return true;
    if (side === 'east' && overlapZ && Math.abs(o.x1 - b.x2) < eps) return true;
  }
  return false;
}

/** 계획 단계. 여기서는 삼각형을 만들지 않는다 */
export function planFacades(): FacadePlan[] {
  return BUILDINGS.map((b) => {
    const bay = BAY[b.archetype];
    const edges: EdgePlan[] = (
      [
        ['north', 'x', b.z1, b.x1, b.x2],
        ['south', 'x', b.z2, b.x1, b.x2],
        ['west', 'z', b.x1, b.z1, b.z2],
        ['east', 'z', b.x2, b.z1, b.z2],
      ] as [Side, 'x' | 'z', number, number, number][]
    ).map(([side, along, at, from, to]) => {
      const len = to - from;
      // 베이 폭은 실제 면 길이에 맞춰 조정한다. 자투리 베이를 붙이지 않는다
      const count = Math.max(2, Math.round(len / bay));
      const w = len / count;
      const bays: Bay[] = [];
      for (let i = 0; i < count; i++) {
        const c = from + w * (i + 0.5);
        const x = along === 'x' ? c : at;
        const z = along === 'x' ? at : c;
        const entrance = b.entrances.some(
          (e) => Math.abs(e.x - x) < w / 2 + 0.1 && Math.abs(e.z - z) < 0.6,
        );
        bays.push({ side, index: i, x, z, width: w, entrance });
      }
      return { side, along, at, from, to, exposed: !hidden(b, side), bays };
    });
    return { id: b.id, archetype: b.archetype, bayWidth: bay, edges };
  });
}

/** 함석 골 한 주기. 21cm 는 실제 골함석 피치다. 타일 하나에 네 골이 들어간다 */
const CORR_TILE = 0.84;

/**
 * 주름 함석의 **결을 재질로** 만든다.
 *
 * 예전에는 벽마다 골을 박스로 100개씩 세웠다(`w.box('corrugated','rib',…)`).
 * 골 앞면 z=29.772, 판 앞면 z=29.82 — **폭 7.5cm 짜리 각재가 10m 에서 1~2px** 로
 * 떨어지고, 앞면 색이 판과 같아서 그늘진 옆면 한 줄만 남았다. 그 한 줄이
 * 에일리어싱으로 끊겨 **「벽에 뿌려진 짧은 파란 막대기」** 가 됐다. 멀어질수록
 * 심해진다 — 화면 공간 문제라 거리로 해결되지 않는다.
 *
 * 밉맵이 있는 텍스처는 반대다. 멀어지면 골이 균일한 회색으로 **녹는다.**
 * 실제 함석도 그렇게 보인다. 골은 1.5cm 깊이라 실루엣에 안 나오므로
 * 지오메트리로 세울 이유가 애초에 없었다.
 */
function corrugationMaps() {
  const S = 256, RIBS = 4;
  const alb = document.createElement('canvas'); alb.width = alb.height = S;
  const nrm = document.createElement('canvas'); nrm.width = nrm.height = S;
  const a = alb.getContext('2d')!, n = nrm.getContext('2d')!;
  const ai = a.createImageData(S, S), ni = n.createImageData(S, S);
  for (let x = 0; x < S; x++) {
    const t = (x / S) * RIBS * Math.PI * 2;
    // 골 단면은 사인파다. 마루가 밝고 골이 어둡다
    const shade = 0.74 + 0.26 * (0.5 + 0.5 * Math.cos(t));
    // 접선공간 법선: 높이 h=cos(t) 의 기울기가 그대로 x 성분이 된다
    const nx = Math.sin(t) * 0.7;
    const nz = Math.sqrt(Math.max(1e-4, 1 - nx * nx));
    for (let y = 0; y < S; y++) {
      const i = (y * S + x) * 4;
      // **가로 이음선을 넣지 마라.** 한 줄 그어 봤더니 화면에서 1px 검은
      // 실선으로 떨어져, 고치려던 「끊긴 선」을 한 겹 더 얹은 꼴이 됐다.
      const v = Math.round(255 * shade);
      ai.data[i] = ai.data[i + 1] = ai.data[i + 2] = v; ai.data[i + 3] = 255;
      ni.data[i] = Math.round((nx * 0.5 + 0.5) * 255);
      ni.data[i + 1] = 128;
      ni.data[i + 2] = Math.round((nz * 0.5 + 0.5) * 255);
      ni.data[i + 3] = 255;
    }
  }
  a.putImageData(ai, 0, 0); n.putImageData(ni, 0, 0);
  const mk = (c: HTMLCanvasElement, srgb: boolean) => {
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 8;
    if (srgb) t.colorSpace = THREE.SRGBColorSpace;
    return t;
  };
  return { map: mk(alb, true), normalMap: mk(nrm, false) };
}

/**
 * 슬롯 하나의 UV 를 **월드 좌표로** 다시 쓴다.
 *
 * `SlotWriter` 는 `BoxGeometry` 를 합쳐 놓으므로 UV 가 면마다 0..1 이다. 그대로
 * 텍스처를 걸면 24m 짜리 벽과 0.9m 짜리 문설주에 **같은 개수의 골**이 생긴다.
 * 법선이 가리키는 축을 빼고 남는 두 축을 그대로 UV 로 쓰면, 조각 크기와 무관하게
 * 골 간격이 월드에서 `tile` 미터로 고정된다.
 */
function projectUV(g: THREE.BufferGeometry, tile: number) {
  const p = g.getAttribute('position'), n = g.getAttribute('normal'), uv = g.getAttribute('uv');
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    let u: number, v: number;
    if (ay >= ax && ay >= az) { u = p.getX(i); v = p.getZ(i); }
    else if (ax >= az) { u = p.getZ(i); v = p.getY(i); }
    else { u = p.getX(i); v = p.getY(i); }
    uv.setXY(i, u / tile, v / tile);
  }
  uv.needsUpdate = true;
}

/**
 * **함석에 때를 입힌다.**
 *
 * `corrugationMaps` 는 0.84m 마다 반복되는 사인파 한 장이라, 벽이 위에서
 * 아래까지 오차 없는 바코드였다 — 재질이 아니라 텍스처 견본. 타일 안에 때를
 * 그리면 그 때도 0.84m 마다 반복된다. 그래서 **월드 좌표로** 넣는다:
 *  - 밑동 때: 눈이 튀고 흙이 묻는 0~0.8m 를 어둡게.
 *  - 판 단위 색차: 0.9m 폭 판마다 ±8%. 함석은 한 장씩 갈아 끼우므로 색이 다르다.
 *  - 녹물 줄: y 로 길게 늘인 노이즈. 위쪽(2m↑)엔 거의 없고 아래로 갈수록 는다.
 * `applySnowAccum` 과 같은 방식(정점에서 월드 좌표를 넘긴다).
 */
function applyTinWeathering(mat: THREE.MeshStandardMaterial) {
  mat.onBeforeCompile = (sh) => {
    sh.vertexShader = 'varying vec3 vTinW;\n' + sh.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      vTinW = (modelMatrix * vec4(transformed, 1.0)).xyz;`,
    );
    sh.fragmentShader = `
      varying vec3 vTinW;
      float tinHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float tinNoise(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        vec2 u2 = f * f * (3.0 - 2.0 * f);
        return mix(mix(tinHash(i), tinHash(i + vec2(1.0, 0.0)), u2.x),
                   mix(tinHash(i + vec2(0.0, 1.0)), tinHash(i + vec2(1.0, 1.0)), u2.x), u2.y);
      }
    ` + sh.fragmentShader
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
      {
        float tAlong = vTinW.x + vTinW.z;
        // 판 단위 색차
        float tPanel = tinHash(vec2(floor(tAlong / 0.9), 3.0));
        diffuseColor.rgb *= 0.92 + 0.16 * tPanel;
        // 밑동 때
        float tGrime = mix(0.58, 1.0, smoothstep(0.05, 0.85, vTinW.y));
        tGrime *= 1.0 - 0.18 * tinNoise(vec2(tAlong * 3.0, vTinW.y * 3.0)) * (1.0 - smoothstep(0.6, 1.4, vTinW.y));
        diffuseColor.rgb *= tGrime;
        // 녹물 줄
        float tR = tinNoise(vec2(tAlong * 5.0, vTinW.y * 0.45)) * 0.7 + tinNoise(vec2(tAlong * 11.0, vTinW.y * 1.2)) * 0.3;
        float tRust = smoothstep(0.66, 0.84, tR) * (1.0 - smoothstep(1.4, 2.6, vTinW.y));
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.30, 0.19, 0.13), tRust * 0.75);
        tinRustOut = tRust;
      }`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
      roughnessFactor = mix(roughnessFactor, 0.95, tinRustOut);`,
      )
      .replace('void main() {', 'float tinRustOut = 0.0;\nvoid main() {');
  };
  mat.customProgramCacheKey = () => 'tinWeather';
  onGpu(mat, (n, m) => n.tinWeathering(m));   // WebGPU 판(gpuNodes.ts)
  return mat;
}

/**
 * **벽에도 같은 겨울을 입힌다** (워크스루 2-3·2-4).
 *
 * 함석은 2-1 에서 풍화를 얻었는데 블록벽·회벽·판벽은 여전히 **줄눈만 있는 판**이었다.
 * 한 마을인데 벽 하나만 겨울을 겪고 나머지는 새 것이면 그 하나가 붕 뜬다.
 * `applyTinWeathering` 과 같은 방식으로 **월드 좌표**를 쓴다 — UV 로 넣으면 타일마다
 * 같은 얼룩이 반복돼 그 자체가 「텍스처 견본」 티를 낸다.
 *
 * 세 가지를 얹는다:
 *  - **얼룩**: 모르타르를 문지른 자국. 큰 무늬 위에 잔 무늬를 얹어 격자 티를 지운다.
 *  - **밑동 습기**: 눈이 튀어 젖는 높이(0~`dampTop`)를 어둡게 하고 **거칠기를 내린다.**
 *    색만 어둡게 하면 그냥 때고, 거칠기가 같이 내려가야 「젖었다」로 읽힌다.
 *  - **세로 물때 줄**: 처마에서 흘러내린 자국. 위쪽에는 거의 없다.
 *
 * 파라미터는 상수로 박아 넣고 `customProgramCacheKey` 를 벽마다 다르게 준다 —
 * 안 그러면 three 가 먼저 컴파일한 프로그램을 재사용해 세 벽이 같은 얼룩을 쓴다.
 */
function applyWallWeathering(
  mat: THREE.MeshStandardMaterial,
  key: string,
  o: { damp: number; dampTop: number; blotch: number; blotchScale: number; streak: number },
) {
  mat.onBeforeCompile = (sh) => {
    sh.vertexShader = 'varying vec3 vWallW;\n' + sh.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      vWallW = (modelMatrix * vec4(transformed, 1.0)).xyz;`,
    );
    sh.fragmentShader = `
      varying vec3 vWallW;
      float wwHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float wwNoise(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        vec2 u2 = f * f * (3.0 - 2.0 * f);
        return mix(mix(wwHash(i), wwHash(i + vec2(1.0, 0.0)), u2.x),
                   mix(wwHash(i + vec2(0.0, 1.0)), wwHash(i + vec2(1.0, 1.0)), u2.x), u2.y);
      }
    ` + sh.fragmentShader
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
      {
        float wa = vWallW.x + vWallW.z;
        float wbl = wwNoise(vec2(wa, vWallW.y) * ${o.blotchScale.toFixed(2)}) * 0.65
                  + wwNoise(vec2(wa, vWallW.y) * ${(o.blotchScale * 2.7).toFixed(2)}) * 0.35;
        diffuseColor.rgb *= 1.0 + ${o.blotch.toFixed(3)} * (wbl - 0.5) * 2.0;
        float wwet = 1.0 - smoothstep(0.05, ${o.dampTop.toFixed(2)}, vWallW.y);
        wwet *= 0.75 + 0.5 * wwNoise(vec2(wa * 2.6, vWallW.y * 5.0));
        wwet = clamp(wwet, 0.0, 1.0);
        diffuseColor.rgb *= mix(1.0, ${o.damp.toFixed(3)}, wwet);
        float wst = wwNoise(vec2(wa * 7.0, vWallW.y * 0.35));
        float wstreak = smoothstep(0.70, 0.88, wst) * (1.0 - smoothstep(1.2, 2.4, vWallW.y));
        diffuseColor.rgb *= 1.0 - ${o.streak.toFixed(3)} * wstreak;
        wallWetOut = wwet;
      }`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
      roughnessFactor = mix(roughnessFactor, roughnessFactor * 0.72, wallWetOut);`,
      )
      .replace('void main() {', 'float wallWetOut = 0.0;\nvoid main() {');
  };
  mat.customProgramCacheKey = () => 'wallWeather_' + key;
  onGpu(mat, (n, m) => n.wallWeathering(m, o));   // WebGPU 판(gpuNodes.ts)
  return mat;
}

/**
 * 지붕 한 장. 색만 갈고 눈 쌓임 파라미터는 공유한다 — 재료가 달라도
 * **눈은 같은 눈이다.** 여기서 갈리면 집마다 눈이 다르게 온 것처럼 보인다.
 */
const roofMat = (color: string) =>
  applySnowAccum(
    new THREE.MeshStandardMaterial({ color, roughness: 0.92 }),
    { flat: 0.4, coverage: 0.84, edge: 0.12, scale: 0.42, color: '#d2dded' },
  );

/** 재질 슬롯. 슬롯마다 지오메트리 하나로 합쳐서 draw call 을 묶는다 */
function slots() {
  return {
    // 표준설계도 학교의 외장은 붉은 벽돌 아니면 흰 페인트였다.
    //
    // **채도를 낮춰 놨다.** #8a5347 은 낮에는 멀쩡한데, 밤에 주황색 가로등과
    // 창의 전구빛을 받으면 그 조명색이 붉은기에 그대로 얹혀서 소방차가 된다.
    // 실제로 정면 컷이 「빨간 띠 두 줄」로 나왔다. 조명이 색을 더한다는 것을
    // 재질 색에서 미리 빼 둔다.
    brick: new THREE.MeshStandardMaterial({ color: '#6f4a43', roughness: 0.92 }),
    granite: new THREE.MeshStandardMaterial({ color: '#6e7076', roughness: 0.82, metalness: 0.04 }),
    tileBand: new THREE.MeshStandardMaterial({ color: '#4a5b63', roughness: 0.35, metalness: 0.06 }),
    concrete: new THREE.MeshStandardMaterial({ color: '#7b7a74', roughness: 0.92 }),
    fascia: new THREE.MeshStandardMaterial({ color: '#3f4249', roughness: 0.7, metalness: 0.2 }),
    /**
     * 주름 함석. **metalness 를 올리면 밤에 새까매진다** — 이 씬은 일부러
     * `scene.environment` 를 안 건다(motion.ts). 반사할 환경이 없는데 금속으로
     * 두면 확산광이 죽어서, 별동 벽에 세운 골 76 개가 통째로 검은 막대가 됐다.
     * 「쇠창살」로 보였던 게 이것이다. 금속 느낌은 거칠기로만 낸다.
     */
    corrugated: applyTinWeathering(new THREE.MeshStandardMaterial({
      /**
       * 맵이 평균 0.63(선형) 을 곱하므로 **바탕색을 그만큼 올려 둔다.**
       * 안 올리면 골을 넣은 순간 별동 벽만 한 단 어두워진다.
       */
      color: '#939ba3', roughness: 0.5, metalness: 0.05,
      ...corrugationMaps(), normalScale: new THREE.Vector2(0.5, 0.5),
    })),
    // 시멘트블록 — 1970년 겨울 전국 3만 3천 이동에 시멘트 335부대씩 무상 배포된
    // 그 시멘트로 쌓은 벽과 담이다. 이 마을 집들의 실제 재료
    block: applyWallWeathering(
      new THREE.MeshStandardMaterial({ color: '#9b9689', roughness: 0.95 }),
      'block', { damp: 0.72, dampTop: 0.85, blotch: 0.11, blotchScale: 1.6, streak: 0.10 },
    ),
    timber: new THREE.MeshStandardMaterial({ color: '#4a3d2e', roughness: 0.9 }),
    /**
     * 밟혀 다져진 눈 — 문 앞과 처마 밑 (워크스루 3-3·3-4).
     * 새 눈(`snow` #c9d6e4)보다 어둡고 파랗다. **바람 자국을 얹지 않는다** —
     * 다져진 눈은 바람이 못 긁는다. 그 차이가 「밟혔다」로 읽히는 신호다.
     */
    snowPacked: new THREE.MeshStandardMaterial({ color: '#9aa8bd', roughness: 0.86 }),
    // 눈둑·처마 눈. 매끈한 단색이면 석고다 — 지형 눈과 같은 필드의 바람 자국을 얹는다
    snow: applySnowAccum(new THREE.MeshStandardMaterial({ color: '#c9d6e4', roughness: 0.96 }),
      { flat: 0.1, coverage: 1.0, edge: 0.01, scale: 0.6, color: '#cfdae8', bump: 0.5 }),

    /* --- 2026 년. 이 년 전에 고쳐서 들어왔다는 사실이 밖에서 보여야 한다 ---
       골조는 1998 년 폐교와 1970 년대 농가 그대로다. 고친 것은 **껍데기뿐**이고,
       그마저 돈이 샜다(인테리어비 1억 8천이 실제와 다르다 — 배정호의 횡령).
       그래서 새 것과 손 안 댄 것이 한 벽에 같이 있어야 한다. */

    /** 알루미늄 샷시. 낡은 목창을 갈아 끼운 것 — 창 하나로 시대가 바뀐다 */
    sash: new THREE.MeshStandardMaterial({ color: '#3d434a', roughness: 0.42, metalness: 0.52 }),
    /**
     * 창 **뒤판** — 불 꺼진 칸.
     *
     * 유리 자체는 `window_slider` 조각이 갖고 있다(슬롯 `pane`, 불투명도 0.28).
     * 그 유리 너머가 아무것도 없는 허공이라 밤에 조합센터 정면이 「붉은 벽에
     * 뚫린 검은 구멍 여덟 개」로 나왔다. **유리는 뒤에 뭔가 있어야 유리로 보인다.**
     * 반투명 유리 뒤에 이 판을 깔아 그 「뭔가」를 만든다.
     */
    glass: new THREE.MeshStandardMaterial({
      /**
       * `#141a24` 는 밤에 **죽은 검정**이었다. 이 씬은 environment 가 없어
       * 광택으로 하늘을 되비칠 수가 없고, 그러면 창이 벽에 뚫린 구멍이 되고
       * 그 앞의 흰 창살만 튀어 「벽에 격자가 그려진 것」으로 읽힌다. 유리는
       * 밤에도 하늘색을 약간 머금는다 — 반구광을 받을 만큼 올린다.
       */
      color: '#2a3648', roughness: 0.3, metalness: 0.0,
    }),
    /**
     * 불 켜진 칸.
     *
     * 22시 마을에서 창이 전부 까맣면 폐가다. 사람이 산다는 것은 **몇 칸만**
     * 켜져 있는 것으로 말한다 — 전부 켜면 그건 학교가 아니라 상가다.
     */
    litGlass: new THREE.MeshStandardMaterial({
      color: '#3a2b18', emissive: '#ffca85', emissiveIntensity: 1.0, roughness: 0.5,
    }),
  /**
   * **같은 밝기로 다 켜면 그것도 가짜다.** 형광등을 켠 교무실과 백열등 하나
   * 켜 둔 숙직실이 같은 밝기일 수 없다. 두 단계로 나눠 놓는다.
   */
  litGlassDim: new THREE.MeshStandardMaterial({
    color: '#2a2114', emissive: '#e0a35e', emissiveIntensity: 0.42, roughness: 0.6,
  }),
    /** 실외기 도장 철판. **시골 건물에 이것 하나면 사람이 사는 2026 년이 된다** */
    hvac: new THREE.MeshStandardMaterial({ color: '#b9bdc1', roughness: 0.58, metalness: 0.28 }),
    /** 팬 그릴 · 통풍구 — 어두워야 구멍으로 읽힌다 */
    grille: new THREE.MeshStandardMaterial({ color: '#23262a', roughness: 0.8, metalness: 0.15 }),
    /** 배관 보온재와 PVC 홈통. 흰색이라 어두운 벽에서 선으로 읽힌다 */
    pipe: new THREE.MeshStandardMaterial({ color: '#e4e2dc', roughness: 0.72 }),
    /** 새로 깐 방부목 데크. 낡은 timber 보다 밝아야 「새로 한 것」이 된다 */
    deck: new THREE.MeshStandardMaterial({ color: '#8a7350', roughness: 0.88 }),
    /**
     * 슬레이트 지붕. **집을 하늘에서 끊어 주는 유일한 선이다.**
     *
     * 이게 없을 때 언덕 위 한서진 집은 밤하늘에 대고 자를 대고 그은 것 같은
     * 평평한 윗변으로 끝났다 — 「언덕 위에 얹힌 실루엣」이 아니라 상자였다.
     *
     * 눈은 `applySnowAccum` 이 면의 방향으로 판정한다. 물매 19° 면의 N.y 는
     * 0.945 라 거의 통째로 얹히고, 처마 끝 두께면·박공 벽은 수직이라 그대로
     * 슬레이트로 남는다. 지붕이 흰 판 한 장이 아니라 **눈 밑에 재료가 비치는**
     * 면이 되는 건 그 대비 때문이다.
     */
    roof: roofMat('#474d55'),

    /* --- 지붕 재료 넷을 더 둔다 ------------------------------------
       집 넷이 같은 모델로 나왔던 이유의 절반이 여기다. 밤에 60m 밖에서
       집을 구분해 주는 것은 창도 굴뚝도 아니고 **지붕 한 면의 색**이다 —
       눈이 덮이고 남은 처마 끝·박공 쪽 띠가 그 색을 드러낸다.

       전부 채도를 낮춰 뒀다. 이 파일이 이미 한 번 배운 것이다(#8a5347 벽돌이
       주황 가로등 밑에서 소방차가 됐다). 함석 빨강을 그대로 #a33 으로 두면
       가로등 밑에서 형광색이 된다. */
    roofTinGreen: roofMat('#3f5a52'),
    roofTinBlue: roofMat('#39485e'),
    roofTinRed: roofMat('#6b4038'),
    roofTile: roofMat('#4e4038'),

    /**
     * 미장 회벽. 시멘트블록 위에 모르타르를 발라 흰색으로 마감한 집 —
     * 블록 줄눈 집보다 **한 단 밝다.** 밤에는 이 밝기 차이가 재료 차이보다
     * 훨씬 멀리서 읽힌다.
     */
    render: applyWallWeathering(
      new THREE.MeshStandardMaterial({ color: '#b0aa9c', roughness: 0.94 }),
      'render', { damp: 0.74, dampTop: 0.90, blotch: 0.07, blotchScale: 2.4, streak: 0.14 },
    ),
    /** 판벽 널. 세로로 촘촘한 그림자 줄이 생겨 면이 아니라 결로 읽힌다 */
    siding: applyWallWeathering(
      new THREE.MeshStandardMaterial({ color: '#6a5b46', roughness: 0.9 }),
      'siding', { damp: 0.70, dampTop: 0.80, blotch: 0.06, blotchScale: 3.0, streak: 0.16 },
    ),
  };
}

type SlotName = keyof ReturnType<typeof slots>;

class SlotWriter {
  private parts = new Map<SlotName, THREE.BufferGeometry[]>();
  counts: Record<string, number> = {};
  /**
   * 지금 쓰고 있는 건물의 바닥 높이. 이 아래 모든 모듈은 y 를 **바닥 기준**으로
   * 적는다 — `WALL_H - 0.28` 은 「처마 밑」이지 「해발 2.6m」가 아니다.
   *
   * 이걸 안 두고 절대 y 로 썼더니, 언덕 위 한서진 집(바닥 3.6m)의 기단·블록
   * 줄눈·서까래·처마·회벽이 통째로 y=0 에 깔렸다. 벽만 3.6m 에 서고 껍데기는
   * 언덕 속에 묻혀서, 축대 틈으로 **집 한 채가 더** 들여다보였다.
   */
  baseY = 0;

  box(slot: SlotName, moduleId: string, w: number, h: number, d: number, x: number, y: number, z: number) {
    const g = new THREE.BoxGeometry(w, h, d);
    g.translate(x, y + this.baseY, z);
    this.push(slot, moduleId, g);
  }

  /**
   * 기울인 판. `box` 는 **평행이동밖에 못 한다** — 지붕 물매처럼 축에 안 붙는
   * 조각은 여기로 온다. 회전은 한 축만 받는다(용마루는 항상 수평이라 그걸로 족하다).
   */
  /**
   * 벽 밑동 눈둑. 벽에서 `D` 만큼 나가며 `H` 에서 0 으로 **오목하게** 내려가고
   * 양 끝 0.6m 는 0 으로 죽는다. 기울인 상자로 했더니 흰 널빤지를 벽에 기대
   * 놓은 꼴이었다 — 눈은 모서리가 없다. 격자 하나를 직접 짠다.
   * `along` 축을 따라 `from..to`, 벽선은 `at`, 바깥은 `out` 방향.
   */
  drift(slot: SlotName, moduleId: string, along: 'x' | 'z', from: number, to: number,
        at: number, out: number, D: number, H: number, taperA = 0.6, taperB = 0.6, inner = 0.13) {
    const L = to - from;
    const nL = Math.max(2, Math.ceil(L / 0.45)) + 1;
    const prof = [0, 0.12, 0.28, 0.48, 0.7, 1.0];
    const nP = prof.length;
    const pos: number[] = [], uv: number[] = [], idx: number[] = [];
    const hash = (a: number, b: number) => {
      const v = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
      return v - Math.floor(v);
    };
    for (let i = 0; i < nL; i++) {
      const u = (i / (nL - 1)) * L;
      /**
       * **끝을 죽이는 길이를 자리마다 다르게 준다** (워크스루 3-2).
       * 예전에는 양 끝 0.6m 를 무조건 0 으로 내렸다 — 벽 끝, 곧 **건물 코너**에서도
       * 죽어서 코너마다 눈 없는 V 자 홈이 생겼다. 문 앞은 밟혀서 안 서는 게 맞지만
       * 코너는 오히려 제일 두껍게 쌓이는 자리다. 0 을 주면 안 죽인다.
       */
      const endT = Math.min(1, taperA > 0 ? u / taperA : 1, taperB > 0 ? (L - u) / taperB : 1);
      const taper = endT * endT * (3 - 2 * endT);
      for (let j = 0; j < nP; j++) {
        // 바깥 두 줄은 가로로도 흔든다 — 안 그러면 둑 가장자리가 자로 그은 직선이 되고,
        // 이 파일이 이미 「눈은 모서리가 없다」로 한 번 배운 것을 가장자리에서 다시 어긴다
        const wob = j >= nP - 2 ? (hash(from + u, j + 7) - 0.5) * 0.22 : 0;
        const d = prof[j] * D + wob;
        const k = 1 - prof[j];
        const noise = 0.85 + 0.3 * hash(from + u, j);
        const h = H * k * k * (1 + 0.6 * k) / 1.6 * taper * (j === 0 ? 1 : noise) + (j === nP - 1 ? -0.02 : 0);
        const a = from + u;
        const b = at + out * (inner + d);
        if (along === 'x') pos.push(a, h + this.baseY, b);
        else pos.push(b, h + this.baseY, a);
        uv.push(u, d);
        if (i < nL - 1 && j < nP - 1) {
          const q = i * nP + j;
          idx.push(q, q + nP, q + 1, q + 1, q + nP, q + nP + 1);
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx);
    g.computeVertexNormals();
    // 감는 방향이 축·바깥 부호에 따라 뒤집힌다. 법선이 아래를 보면 뒤집어 다시 잰다
    const n = g.getAttribute('normal');
    if (n.getY(Math.floor(n.count / 2)) < 0) {
      g.setIndex(idx.reverse());
      g.computeVertexNormals();
    }
    this.push(slot, moduleId, g);
  }

  tilt(slot: SlotName, moduleId: string, w: number, h: number, d: number,
       x: number, y: number, z: number, axis: 'x' | 'z', rad: number) {
    const g = new THREE.BoxGeometry(w, h, d);
    if (axis === 'x') g.rotateX(rad);
    else g.rotateZ(rad);
    g.translate(x, y + this.baseY, z);
    this.push(slot, moduleId, g);
  }

  /**
   * 박공 끝의 삼각 벽. `BoxGeometry` 로 만들 수 없는 유일한 조각이라 직접 짠다.
   * 밑변 `2*halfW`, 높이 `h`, 두께 `t`, 원점은 밑변 한가운데.
   *
   * `mergeAll` 이 position/normal/uv/index 를 그대로 훑으므로 **넷 다 채운다.**
   * 꼭짓점을 면마다 복제해서 인덱스가 겹치지 않으므로 `computeVertexNormals`
   * 가 평평한 법선을 준다 — 물매면과 박공면의 경계가 뭉개지면 안 된다.
   */
  gable(slot: SlotName, moduleId: string, halfW: number, h: number, t: number,
        x: number, y: number, z: number, rotY: number) {
    const hw = halfW, ht = t / 2;
    const A = [-hw, 0, ht], B = [hw, 0, ht], C = [0, h, ht];
    const D = [-hw, 0, -ht], E = [hw, 0, -ht], F = [0, h, -ht];
    const faces = [
      [A, B, C], [E, D, F],           // 앞 · 뒤
      [D, A, C], [D, C, F],           // 왼쪽 물매
      [B, E, F], [B, F, C],           // 오른쪽 물매
      [D, E, B], [D, B, A],           // 밑
    ];
    const pos: number[] = [];
    for (const f of faces) for (const v of f) pos.push(v[0], v[1], v[2]);
    const n = pos.length / 3;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
    g.setIndex(Array.from({ length: n }, (_, i) => i));
    g.computeVertexNormals();
    if (rotY) g.rotateY(rotY);
    g.translate(x, y + this.baseY, z);
    this.push(slot, moduleId, g);
  }

  private push(slot: SlotName, moduleId: string, g: THREE.BufferGeometry) {
    const list = this.parts.get(slot);
    if (list) list.push(g);
    else this.parts.set(slot, [g]);
    this.counts[moduleId] = (this.counts[moduleId] ?? 0) + 1;
  }

  flush(root: THREE.Group, mats: ReturnType<typeof slots>) {
    let tris = 0;
    for (const [slot, geos] of this.parts) {
      const merged = geos.length === 1 ? geos[0] : mergeAll(geos);
      if (!merged) continue;
      // 함석만 월드 UV 로 갈아 끼운다. 나머지 슬롯은 맵이 없어 UV 를 안 본다
      if (slot === 'corrugated') projectUV(merged, CORR_TILE);
      const mesh = new THREE.Mesh(merged, mats[slot]);
      // 부감(overview)에서 지붕만 걷어내야 해서 슬롯을 남긴다
      mesh.userData.slot = slot;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      mesh.matrixAutoUpdate = false;
      root.add(mesh);
      tris += (merged.index?.count ?? 0) / 3;
    }
    this.parts.clear();
    return tris;
  }
}

function mergeAll(geos: THREE.BufferGeometry[]): THREE.BufferGeometry | null {
  // three 의 mergeGeometries 를 쓰되, 이 파일은 BoxGeometry 만 쓰므로 속성이 항상 같다
  const merged = geos[0].clone();
  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  let base = 0;
  for (const g of geos) {
    const p = g.getAttribute('position');
    const n = g.getAttribute('normal');
    const u = g.getAttribute('uv');
    for (let i = 0; i < p.count; i++) {
      pos.push(p.getX(i), p.getY(i), p.getZ(i));
      nor.push(n.getX(i), n.getY(i), n.getZ(i));
      uv.push(u.getX(i), u.getY(i));
    }
    const gi = g.getIndex()!;
    for (let i = 0; i < gi.count; i++) idx.push(gi.getX(i) + base);
    base += p.count;
    g.dispose();
  }
  merged.dispose();
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  out.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  out.setIndex(idx);
  return out;
}

/** 면 하나에 원형별 외피를 붙인다 */
function cladEdge(w: SlotWriter, plan: FacadePlan, b: BuildingDef, e: EdgePlan) {
  const along = e.along;
  const len = e.to - e.from;
  const at = e.at;
  // 외피는 **벽 바깥으로만** 내민다. 대칭으로 두면 실내에 기단과 처마가 튀어나온다
  const out = e.side === 'north' || e.side === 'west' ? -1 : 1;
  const inner = 0.15; // 벽 두께의 절반

  /**
   * 이 면에 **실제로 뚫린** 구멍. `b.entrances` 를 믿으면 안 된다 —
   * 거기엔 대표 현관 하나만 적혀 있어서, 조합센터 북쪽 뒷문(`doorsN:[0]`)처럼
   * `WALLS` 에만 있는 문은 통째로 빠졌다. 그래서 벽돌 허리띠가 뒷문을
   * 가로질러 흐르고 교실 기둥이 문 한가운데 서 있었다.
   * **구멍을 뚫은 쪽에서 그대로 읽어 온다.**
   */
  const spansOf = (kind: 'door' | 'window') => {
    const found: { c: number; half: number }[] = [];
    for (const wd of WALLS) {
      if (wd.gap !== kind) continue;
      const onLine = along === 'x'
        ? Math.abs(wd.z1 - at) < 0.3 && Math.abs(wd.z2 - at) < 0.3
        : Math.abs(wd.x1 - at) < 0.3 && Math.abs(wd.x2 - at) < 0.3;
      if (!onLine) continue;
      const p0 = along === 'x' ? wd.x1 : wd.z1;
      const p1 = along === 'x' ? wd.x2 : wd.z2;
      const lo = Math.min(p0, p1);
      const hi = Math.max(p0, p1);
      if (hi < e.from - 0.1 || lo > e.to + 0.1) continue;
      const c = (lo + hi) / 2;
      if (found.some((f) => Math.abs(f.c - c) < 0.05)) continue; // 인방/허리벽 두 조각
      found.push({ c, half: (hi - lo) / 2 });
    }
    return found.sort((p, q) => p.c - q.c);
  };
  const doorSpans = spansOf('door');
  /**
   * 문 + 창. 세로로 지나가는 것(기둥·널)은 이걸 전부 피해야 한다.
   *
   * **반드시 정렬한다.** `some()` 으로 훑는 쪽은 순서를 안 타지만, 구간을 왼쪽부터
   * 잘라 나가는 `clad` 는 탄다 — 문(-10, 2) 뒤에 창(-6) 이 붙으면 `cur` 이 2 를
   * 지난 뒤라 창 구간이 음수 길이로 버려지고, 판이 **창을 통째로 덮었다.**
   */
  const openSpans = [...doorSpans, ...spansOf('window')].sort((p, q) => p.c - q.c);

  /**
   * 바깥으로 `thick` 만큼 나오는 띠. 안쪽 면은 벽 안쪽면에 맞춘다.
   * `skipDoors` 면 현관 구간을 비우고 좌우로 잘라서 낸다.
   */
  const clad = (slot: SlotName, id: string, h: number, y: number, thick: number, skipDoors = false, skipOpen = false) => {
    // 벽 **바깥면에서 시작해** thick 만큼 나온다. 2cm 는 벽 속으로 물려서
    // 같은 평면이 생기지 않게 한다.
    //
    // 예전 식(`d = inner + thick`, `off = out*(thick-inner)/2`)은 얇은 띠일 때
    // 판의 뒷면이 **벽의 반대쪽 면과 정확히 같은 평면**에 놓였다. 그래서 실내에서
    // 벽이 지지직거렸고, 정작 밖에서는 띠가 벽 속에 묻혀 안 보였다.
    const d = thick + 0.04;
    const off = out * (inner + thick / 2 - 0.02);
    const runs: [number, number][] = [];
    const cuts = skipOpen ? openSpans : skipDoors ? doorSpans : [];
    if (cuts.length) {
      let cur = e.from;
      for (const sp of cuts) {
        runs.push([cur, sp.c - sp.half - 0.05]);
        cur = sp.c + sp.half + 0.05;
      }
      runs.push([cur, e.to]);
    } else {
      runs.push([e.from, e.to]);
    }
    for (const [a2, b2] of runs) {
      const L = b2 - a2;
      if (L < 0.05) continue;
      const mid = (a2 + b2) / 2;
      const x = along === 'x' ? mid : at + off;
      const z = along === 'x' ? at + off : mid;
      w.box(slot, id, along === 'x' ? L : d, h, along === 'x' ? d : L, x, y, z);
    }
  };

  const plinth = PLINTH[plan.archetype];
  if (plinth > 0) clad('concrete', 'plinth', plinth, plinth / 2, 0.26, true);

  /**
   * **벽 밑동의 눈둑.**
   *
   * 눈밭이 벽을 자로 그은 직선으로 만났다 — 20cm 눈이 왔는데 벽에 몰린 것도,
   * 구석에 쌓인 것도 없어서 건물이 눈 위에 「놓인」 게 아니라 「합성된」 것으로
   * 보였다(review-scene 체크리스트 6번의 fillet). 벽에서 0.9m 나가며 내려가는
   * 오목한 둑 하나다(`SlotWriter.drift`). 문 앞은 비운다 — 드나드는 곳은
   * 밟혀서 눈둑이 안 선다.
   */
  {
    const D = 0.95;
    /**
     * **코너 정사각형은 두 벽 어느 둑도 안 덮는다.** 남벽 둑은 x 가 코너에서
     * 시작하고 서벽 둑은 z 가 코너에서 시작하니 코너 바깥 D×D 가 통째로 빈다.
     * 그래서 **x 축 둑만** 코너 밖으로 내밀어 L 자로 물린다 — 양 축을 다 내밀면
     * 겹쳐서 z-파이팅이 난다.
     *
     * 내민 끝은 **내민 길이만큼 죽인다.** 안 죽이면 코너를 지나 허공에서 뚝 끊긴
     * 평평한 판이 된다(첫 시도가 실제로 그랬다). 죽이는 길이를 `lap` 으로 주면
     * 높이가 0 이 되는 지점이 정확히 내민 끝이고 **코너 자리에서는 아직 만높이**다.
     * z 축 둑은 안 내밀고 코너 끝을 **안 죽인다**(0) — 죽이면 x 둑이 못 덮는
     * 벽 바로 옆에서 꺼진다.
     */
    /**
     * **처마 밑에는 눈이 안 쌓인다** (워크스루 3-4).
     * 눈둑을 벽에서 0.13m 부터 시작했더니 처마 아래까지 눈이 차 있었다 — 처마가
     * 있으나 마나였다. 고드름을 다는 것보다 **이 마른 띠 하나가 처마를 읽힌다.**
     * 둑을 처마 끝에서 시작하고 그 안쪽은 다져진 눈으로 깐다.
     */
    const eaveW = EAVE[plan.archetype];
    const lap = along === 'x' ? D + eaveW : 0;
    const END = -1;   // 「벽 끝(코너)」 표시
    const runs: [number, number, number, number][] = [];
    let cur = e.from;
    for (const sp of doorSpans) {
      runs.push([cur, sp.c - sp.half - 0.25, cur === e.from ? END : 0.6, 0.6]);
      cur = sp.c + sp.half + 0.25;
    }
    runs.push([cur, e.to, cur === e.from ? END : 0.6, END]);
    for (const [a2, b2, tA, tB] of runs) {
      if (b2 - a2 < 1.2) continue;
      w.drift('snow', 'wall-drift', along,
        tA === END ? a2 - lap : a2 + 0.2,
        tB === END ? b2 + lap : b2 - 0.2,
        at, out, D, 0.36,
        tA === END ? lap : tA, tB === END ? lap : tB, eaveW);
    }
    // 처마 밑 마른 띠 — 벽선에서 처마 끝까지
    {
      const cB = at + out * (eaveW / 2);
      const len = e.to - e.from;
      const mid = (e.from + e.to) / 2;
      if (along === 'x') w.box('snowPacked', 'eave-dry', len, 0.02, eaveW, mid, 0.01, cB);
      else w.box('snowPacked', 'eave-dry', eaveW, 0.02, len, cB, 0.01, mid);
    }
    /**
     * **문 앞은 밟혀 다져진다** (워크스루 3-3).
     * 눈둑은 이미 문 앞을 비우는데, 비운 자리가 그냥 새 눈이라 「드나든 자국」이
     * 없었다. 문 폭보다 조금 넓게, 밖으로 1.2m 눌린 눈을 깐다. 처마 띠보다 1mm
     * 높여서 겹치는 자리에서 깜빡이지 않게 한다.
     */
    for (const sp of doorSpans) {
      const wide = sp.half * 2 + 0.6;
      const deep = 1.2;
      const cD = at + out * (deep / 2);
      if (along === 'x') w.box('snowPacked', 'door-tramp', wide, 0.02, deep, sp.c, 0.011, cD);
      else w.box('snowPacked', 'door-tramp', deep, 0.02, wide, cD, 0.011, sp.c);
    }
  }

  const band = TILE_BAND[plan.archetype];
  if (band) clad('tileBand', 'tile-band', band[1] - band[0], (band[0] + band[1]) / 2, 0.2);

  // 처마 밑 갈매기판 — 지붕과 벽 사이에 그림자 선을 하나 긋는다
  clad('fascia', 'fascia', 0.2, WALL_H - 0.05, EAVE[plan.archetype]);

  if (plan.archetype === 'shed') {
    /**
     * 함석 벽. **판을 먼저 씌운다.**
     *
     * 벽 본체는 레벨 전체가 회벽이다(`world.ts` 의 `plaster`). 그 위에 금속 골만
     * 세우면 「회벽 앞에 세워 놓은 세로 창살」로 읽힌다 — 실제로 그렇게 보였다.
     * 창고 벽은 함석 **그 자체**이므로 얇은 판으로 면을 먼저 덮고, 골은 그 판의
     * 주름으로 얹는다. 문·창은 판도 골도 비킨다.
     */
    /**
     * **가마실은 블록 벽이다.** 레벨이 `wall: 'block'` 이라고 적어 놨는데
     * (「불을 때는 칸이라 벽이 콘크리트 블록」) 여기서 archetype 만 보고 함석을
     * 씌워 버렸다. 그래서 본채와 가마실이 같은 줄무늬 상자 둘로 나왔다 —
     * 「두 채로 읽히게 재료를 바꾼다」던 설계가 화면에 없었다. 집과 같은
     * 블록 줄눈으로 낸다.
     */
    if (styleOf(b).wall === 'block') {
      for (let y = 0.55; y < WALL_H - 0.4; y += 0.57) {
        clad('block', 'block-course', 0.05, y, 0.03, y < 2.25);
      }
      return;
    }
    const skinTop = WALL_H - 0.35;
    const HEAD = 2.2; // 문 인방. shared 의 DOOR_HEAD 와 같다
    /**
     * 개구부는 **가로만** 비켜야 한다. 판 한 장으로 덮으면서 문·창 폭을 통째로
     * 잘라내면 창 위아래로 회벽이 세로 띠처럼 드러난다 — 실제로 그랬다.
     * 창턱 아래 / 창 높이 / 인방 위로 나눠 붙여서 잘리는 곳을 창 높이에 가둔다.
     */
    /**
     * **띠끼리 면을 공유시키지 마라.** 네 장을 정확히 맞물려 쌓았더니 아래판의
     * 윗면과 윗판의 밑면이 같은 평면에 놓여, 창턱 높이(y=1.0)에 **1px 검은
     * 실선**이 벽 전체를 가로질렀다. Chris 가 「이런 결」이라고 짚은 그 선이다.
     *
     * 실제 함석은 아래판이 더 나오게 **겹쳐 친다**(물이 이음매로 안 들어가게).
     * 위로 갈수록 2mm 씩 얇게 두고, 윗판 밑동을 2cm 내려 아래판 뒤로 물린다.
     * 그러면 공유하는 면이 없어지고, 남는 것은 물매를 타는 2mm 턱 하나뿐이다.
     */
    const lap = 0.02;
    clad('corrugated', 'tin-skin', WIN_Y.sill, WIN_Y.sill / 2, 0.034, true);
    clad('corrugated', 'tin-skin', WIN_Y.head - WIN_Y.sill + lap,
      (WIN_Y.sill - lap + WIN_Y.head) / 2, 0.032, false, true);
    clad('corrugated', 'tin-skin', HEAD - WIN_Y.head + lap,
      (WIN_Y.head - lap + HEAD) / 2, 0.03, true);
    if (skinTop > HEAD) {
      clad('corrugated', 'tin-skin', skinTop - HEAD + lap, (HEAD - lap + skinTop) / 2, 0.028);
    }

    /**
     * 주름은 **`corrugated` 재질이 갖고 있다**(`corrugationMaps`). 여기서
     * 박스로 세우지 않는다 — 76개를 세워 봤고, 10m 에서 1~2px 로 떨어져
     * 「벽에 뿌려진 짧은 파란 막대기」가 됐다. 골 깊이는 손가락 한 마디라
     * 실루엣에 안 나오므로 지오메트리로 세울 이유가 없다.
     *
     * 문·창을 비키는 일도 저절로 해결된다. 판이 안 붙은 곳엔 결도 없다.
     */
    return;
  }

  if (plan.archetype === 'house') {
    const st = styleOf(b);

    /* --- 벽 마감. 집을 가르는 두 번째 축이다 -----------------------
       지붕 색이 「멀리서」 집을 가른다면 이건 「가까이서」 가른다. 셋 다
       같은 시멘트블록 골조 위에 올라간 것이라 평면·치수는 그대로다 —
       1970년대 이 마을 집들이 실제로 그랬다. 뼈대는 같고 마감만 다르다. */
    if (st.wall === 'block') {
      // 시멘트블록 줄눈 — 블록 한 켜가 19cm 다. 세 켜마다 한 줄씩 그어서 재료를 읽힌다
      for (let y = 0.55; y < WALL_H - 0.4; y += 0.57) {
        clad('block', 'block-course', 0.05, y, 0.03, y < 2.25);
      }
    } else if (st.wall === 'render') {
      /**
       * 미장 회벽. 벽 전체를 한 판으로 덮으면 **창이 통째로 메워진다** —
       * 학교 정면에서 이미 한 번 겪은 사고라 여기서도 창턱 아래와 인방 위로
       * 나눠 붙인다. 밝은 회색이라 이 두 띠 사이의 창 줄이 오히려 또렷해진다.
       */
      const belowH = WIN_Y.sill - PLINTH.house;
      if (belowH > 0.1) clad('render', 'render-below', belowH, PLINTH.house + belowH / 2, 0.05, true);
      const aboveH = WALL_H - WIN_Y.head - 0.22;
      if (aboveH > 0.1) clad('render', 'render-above', aboveH, WIN_Y.head + aboveH / 2, 0.05);
    } else {
      /**
       * 판벽. 세로 널을 32cm 마다 세운다 — 창고(`shed`)의 함석 골보다 성기고
       * 굵어서 금속이 아니라 나무로 읽힌다. 널 사이 그림자가 면을 결로 바꾼다.
       */
      const n = Math.max(3, Math.floor(len / 0.32));
      for (let i = 0; i < n; i++) {
        const t = e.from + (len / n) * (i + 0.5);
        if (openSpans.some((sp) => Math.abs(t - sp.c) < sp.half + 0.1)) continue;
        const x = along === 'x' ? t : at + out * 0.17;
        const z = along === 'x' ? at + out * 0.17 : t;
        const [pw, pd] = along === 'x' ? [0.11, 0.1] : [0.1, 0.11];
        const h = WALL_H - PLINTH.house - 0.3;
        w.box('siding', 'siding-board', pw, h, pd, x, PLINTH.house + h / 2, z);
      }
    }

    // 서까래 끝 — 처마 밑에 나무 토막이 베이마다 하나씩
    for (const bay of e.bays) {
      const x = along === 'x' ? bay.x : at + out * 0.32;
      const z = along === 'x' ? at + out * 0.32 : bay.z;
      const [rw, rd] = along === 'x' ? [0.09, 0.56] : [0.56, 0.09];
      w.box('timber', 'rafter', rw, 0.11, rd, x, WALL_H - 0.28, z);
    }

    /**
     * 툇마루. 현관이 있는 면에만 놓는다.
     *
     * 집 넷이 같아 보였던 이유 중 남은 하나가 **현관 앞이 전부 맨 벽**이라는
     * 것이었다. 마루 한 장과 기둥 두 개가 서면 집이 앉은 높이(기단 45cm)가
     * 눈에 보이고, 밤에 처마 그늘이 이 깊이만큼 생겨 정면이 평면에서 벗어난다.
     */
    if (st.porch && doorSpans.length) {
      const D = 1.3;                       // 마루 깊이
      const halfL = 1.85;                  // 문 좌우로 나오는 길이
      const deckY = PLINTH.house;
      for (const { c, half } of doorSpans) {
        /**
         * **마루를 문 앞으로 통과시키지 마라.** 한 장(3.7m)으로 깔았더니 마루 윗면
         * (기단 높이 0.45)이 현관문을 **가로로 잘라먹었다** — 문짝 2.14m 중 아래
         * 45cm 가 마루 속에 묻혀서 손잡이가 무릎 높이에 붙은 것처럼 보였다.
         * Chris 가 「문이 바닥에 잠겼냐」고 짚은 것이 이것이다.
         *
         * 원인은 마루가 아니라 **바닥 높이가 하나뿐**이라는 것이다. 실내 바닥도
         * 마당도 전부 `baseY`(한서진 집은 3.6)에 있고 — 광선으로 확인했다,
         * 실내 바닥 메시 윗면 y=3.6, 플레이어 pos.y=3.6 — 기단 0.45 는 벽 밑에
         * 두른 «띠»일 뿐 바닥을 올리지 않는다. 그러니 45cm 위에 깔린 마루는
         * 문짝·플레이어 둘 다와 겹친다.
         *
         * 바닥을 올리는 건 방·가구·충돌·스폰을 전부 건드리는 일이라 하지 않는다.
         * 대신 «봉당» 구성을 쓴다 — 현관 앞은 땅 높이 그대로 두고(신 신고 드나드는
         * 자리), 마루는 그 좌우에만 깐다. 옛 집이 실제로 이 구성이고, 무엇보다
         * **기단 띠가 이미 같은 폭으로 문 옆을 잘라내고 있다**(`clad(..., true)`).
         * 같은 절단선을 쓰면 기단 노치와 마루 끝이 한 줄로 맞는다.
         */
        const gapH = half + 0.05;          // 기단이 문 옆을 자르는 지점과 같게
        const pl = halfL - gapH;           // 한쪽 마루 폭
        const off = out * (inner + D / 2);
        for (const sgn of [-1, 1]) {
          const pc = c + sgn * (gapH + pl / 2);
          const px = along === 'x' ? pc : at + off;
          const pz = along === 'x' ? at + off : pc;
          const [dw, dd] = along === 'x' ? [pl, D] : [D, pl];
          w.box('deck', 'porch-deck', dw, 0.16, dd, px, deckY - 0.08, pz);
          /**
           * 마루 밑 기단. 이게 없으면 널판이 땅에서 29cm 떠 있는 **공중 판때기**다
           * (밑이 그냥 뚫려 있었다). 6cm 물려 넣어 마루가 처마처럼 내밀게 하면
           * 그 그늘이 «경계선» 노릇을 한다 — 면을 맞추면 선이 안 생긴다.
           */
          const sk = 0.06, sh = deckY - 0.16;
          if (sh > 0.05) {
            const [sw, sd] = along === 'x' ? [pl - sk * 2, D - sk] : [D - sk, pl - sk * 2];
            const soff = out * (inner + (D - sk) / 2);
            const sx = along === 'x' ? pc : at + soff;
            const sz = along === 'x' ? at + soff : pc;
            w.box('concrete', 'porch-base', sw, sh, sd, sx, sh / 2, sz);
          }
        }
        // 기둥 둘 — 마루 바깥 모서리에 선다. 이 둘이 처마 밑을 「칸」으로 만든다
        for (const sgn of [-1, 1]) {
          const qoff = out * (inner + D - 0.12);
          const qx = along === 'x' ? c + sgn * (halfL - 0.14) : at + qoff;
          const qz = along === 'x' ? at + qoff : c + sgn * (halfL - 0.14);
          const h = WALL_H - 0.42 - deckY;
          w.box('timber', 'porch-post', 0.13, h, 0.13, qx, deckY + h / 2, qz);
        }
        // 기둥머리를 잇는 보. 없으면 기둥 둘이 허공에 꽂힌 막대가 된다
        const boff = out * (inner + D - 0.12);
        const bx = along === 'x' ? c : at + boff;
        const bz = along === 'x' ? at + boff : c;
        const [bw, bd] = along === 'x' ? [halfL * 2 - 0.1, 0.14] : [0.14, halfL * 2 - 0.1];
        w.box('timber', 'porch-beam', bw, 0.16, bd, bx, WALL_H - 0.34, bz);
      }
    }
    return;
  }

  // 학교: 교실 칸을 가르는 벽기둥. 7.5m 마다 하나 — 이게 교실 경계다.
  // 창 사이의 가는 창틀 기둥이 아니라 **구조 기둥**이라 굵다.
  const n = e.bays.length;
  for (let i = 0; i <= n; i++) {
    const t = e.from + (len / n) * i;
    if (t < e.from + 0.2 || t > e.to - 0.2) continue;
    const [pw, pd] = along === 'x' ? [0.5, 0.22] : [0.22, 0.5];
    /**
     * **구멍 한가운데 기둥이 서 있었다.** 조합센터 뒷문(x=0)이 교실 경계와
     * 정확히 겹쳐서, 문틀 한복판을 폭 50cm 짜리 붉은 벽돌 기둥이 막았다.
     * 7.5m 리듬보다 구멍이 우선이다 — 겹치면 기둥을 뺀다.
     */
    if (openSpans.some((sp) => Math.abs(t - sp.c) < sp.half + (along === 'x' ? pw : pd) / 2)) continue;
    const x = along === 'x' ? t : at + out * 0.11;
    const z = along === 'x' ? at + out * 0.11 : t;
    const h = WALL_H - PLINTH.school;
    w.box('brick', 'classroom-pier', pw, h, pd, x, PLINTH.school + h / 2, z);
  }
  // 벽면을 붉은 벽돌로 덮되 **창 자리는 비운다**. 면 전체를 한 판으로 덮었더니
  // 교실 창이 통째로 붉은 판이 됐다.
  const belowH = WIN_Y.sill - PLINTH.school;
  if (belowH > 0.1) clad('brick', 'brick-below', belowH, PLINTH.school + belowH / 2, 0.05, true);
  // 창 위는 **회벽**이다. 표준설계 학교는 벽돌 허리 위를 흰 모르타르로 발랐다.
  // 위아래를 다 벽돌로 덮었더니 정면이 통째로 붉은 판이 되고(실제로 그랬다)
  // 창의 가로 리듬도 안 읽혔다. 띠 색이 갈리면 그 경계가 곧 창 높이 선이 된다.
  const aboveH = WALL_H - WIN_Y.head - 0.2;
  if (aboveH > 0.1) clad('block', 'stucco-above', aboveH, WIN_Y.head + aboveH / 2, 0.05);
}

/**
 * 문패. 밤에 눈 오는 마을에서 여섯이 12분 안에 흩어져야 한다 —
 * 어느 집이 누구 집인지 **밖에서 읽혀야** 게임이 굴러간다.
 * 크라임씬 세트가 방마다 이름을 붙여 두는 이유가 이것이다.
 *
 * 글자는 런타임에 캔버스로 굽는다. 외부 에셋이 아니다.
 */
function plateTexture(text: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 320;
  c.height = 80;
  const g = c.getContext('2d')!;
  g.fillStyle = '#171c22';
  g.fillRect(0, 0, c.width, c.height);
  g.strokeStyle = '#5d6772';
  g.lineWidth = 4;
  g.strokeRect(4, 4, c.width - 8, c.height - 8);
  g.fillStyle = '#e9e3d6';
  g.font = 'bold 42px "Noto Sans KR", system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text, c.width / 2, c.height / 2 + 2);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/**
 * 창 뒤판 — 밤에 그 칸에 불이 켜져 있는가.
 *
 * **여기서 창틀을 만들지 않는다.** 틀도 유리도 `window_slider` 조각이 이미
 * 갖고 있고, `world.ts` 가 창 하나마다 그것을 굽는다. 예전에는 여기서 틀
 * 다섯 개를 또 세웠다 — 벽 두께보다 6cm 더 깊고(0.36 vs 0.11) 색도 더 어두워서
 * 알루미늄 틀을 완전히 덮었다. 그 결과가 정면의 「검고 두꺼운 구멍 여덟 개」다.
 *
 * 남은 문제는 유리 너머가 허공이라는 것이었다. 반투명 유리(불투명도 0.28)는
 * 뒤에 뭔가 있어야 유리로 보인다. 그 뒤판만 여기서 넣는다.
 */
/** 켜진 창 바깥 눈밭에 깔 빛 자국. `sashes` 가 채우고 `buildFacades` 가 굽는다 */
const litSpill: { x: number; y: number; z: number; rx: number; rz: number; dim: boolean }[] = [];

function sashes(w: SlotWriter) {
  litSpill.length = 0;
  for (const win of WINDOWS) {
    // **창턱 높이는 창마다 다르다.** 언덕 위 집은 바닥이 3.6m 올라가 있는데
    // 여기서 WIN_Y.sill 을 그대로 쓰면 그 집 창만 땅바닥에 남는다.
    const y0 = win.y ?? WIN_Y.sill;
    const y1 = y0 + (WIN_Y.head - WIN_Y.sill);
    const half = win.half ?? WIN_Y.half;

    // 켜진 칸은 **한 칸씩 뭉쳐서** 정한다 — 창 하나하나를 따로 굴리면 켜짐과
    // 꺼짐이 얼룩덜룩 번갈아 나와서 크리스마스 전구가 된다. 교실 한 칸(7.5m)
    // 단위로 묶으면 「저 교실은 불이 켜져 있다」로 읽힌다.
    const bay = Math.floor(win.x / 7.5) * 31 + Math.floor(win.z / 7.5) * 17;
    const b5 = ((bay % 5) + 5) % 5;
    /**
     * **집마다 불이 다르게 켜져야 한다.** 자리 해시만 쓰면 어느 집이 밝은지는
     * 좌표가 정하고, 그래서 배정호 집이 마을에서 제일 밝게 나왔다 —
     * 브리핑은 정확히 그 반대(「어느 집보다 어둡다」)를 요구한다.
     * 창이 어느 건물 것인지 되짚어서 그 집의 `lit` 을 먼저 본다.
     */
    const owner = BUILDINGS.find((q) =>
      win.x > Math.min(q.x1, q.x2) - 0.4 && win.x < Math.max(q.x1, q.x2) + 0.4 &&
      win.z > Math.min(q.z1, q.z2) - 0.4 && win.z < Math.max(q.z1, q.z2) + 0.4);
    const lit = owner?.style?.lit;
    const mat = lit === 'dark' ? 'glass'
      : lit === 'dim' ? (b5 === 0 ? 'litGlassDim' : 'glass')
      : b5 === 0 ? 'litGlass' : b5 === 1 ? 'litGlassDim' : 'glass';

    /**
     * **바깥이 어느 쪽인지 먼저 정한다.** 뒤판도 빛 자국도 이 부호가 필요하다.
     * 예전에는 빛 자국 블록 안에서만 구해서, 뒤판은 방향을 모른 채 벽 한가운데
     * (`win.z`)에 놓였다 — `window_slider` 조각의 유리가 벽 바깥으로 2.1cm 나와
     * 있는데 뒤판이 벽 중심에 있으면 벽이 -Z 를 보는 면에서는 **뒤판이 유리보다
     * 앞**이 된다. 그래서 가마실 창이 자리마다 앞뒤가 뒤바뀌며 세로 이음매를
     * 만들었다(`__dbg.pick`: 왼쪽은 뒤판이 4cm 앞, 오른쪽은 같은 깊이).
     */
    const cx = owner ? (Math.min(owner.x1, owner.x2) + Math.max(owner.x1, owner.x2)) / 2 : 0;
    const cz = owner ? (Math.min(owner.z1, owner.z2) + Math.max(owner.z1, owner.z2)) / 2 : 0;
    const sgn = win.along === 'x' ? Math.sign(win.z - cz) || 1 : Math.sign(win.x - cx) || 1;

    // **켜진 창은 바닥에도 자국을 남긴다.** 창만 밝히고 눈밭을 그대로 두면
    // 빛이 유리에 그려진 그림이 되고, 마을은 밤에 스티커를 붙인 판이 된다.
    // 새어 나온 빛을 창 바깥 눈밭에 깔아 준다 — 데칼 한 장이라 공짜다
    if (mat !== 'glass') {
      litSpill.push({
        x: win.x + (win.along === 'x' ? 0 : sgn * 1.9),
        z: win.z + (win.along === 'x' ? sgn * 1.9 : 0),
        y: (owner?.baseY ?? 0) + 0.014,
        rx: win.along === 'x' ? half * 2.1 : 2.5,
        rz: win.along === 'x' ? 2.5 : half * 2.1,
        dim: mat === 'litGlassDim',
      });
    }

    // 뒤판을 **한 장으로 깔지 않는다.** 한 장이면 불 켜진 창이 크림색 사각형
    // 하나가 되고, 그건 교실 창이 아니라 상가 쇼윈도다. 실제로 그렇게 나왔다.
    // 살대 자리를 비우고 조각으로 나눠 깔면 그 **빈 줄이 곧 창살**이다 —
    // 뒤가 비어 있으니 밤에는 검은 선으로 떨어진다. 살대를 따로 세울 필요가 없다.
    //
    // 나누는 비율은 미서기창이다: 아래 넉 짝이 크고 위 고정창이 작다.
    const COLS = 4;
    const GAP = 0.045;
    const W = half * 2 - 0.08;
    const H = y1 - y0 - 0.08;
    const cw = (W - GAP * (COLS - 1)) / COLS;
    const hLow = (H - GAP) * 0.67;
    const hUp = H - GAP - hLow;

    /**
     * **살대 자리는 뚫린 구멍이다.** 뒤판을 조각으로 나눠 깔면 그 빈 줄이 창살처럼
     * 보인다는 것이 원래 의도였는데, 그건 **뒤가 어두울 때만** 맞다. 방에 불이
     * 켜지면 그 줄로 내부가 그대로 새어 나와 노란 십자가 선다 — 가마실 창이
     * 「벌어졌다」고 지적받은 것이 이것이다(2026-09-06 Chris).
     *
     * 개구부 전체를 덮는 **어두운 뒤판 한 장**을 더 깊이 깐다. 살대 줄은 이제
     * 뚫린 구멍이 아니라 어두운 판이라 여전히 검은 선으로 읽히고, 새는 곳은 없다.
     * 뒤판 격자보다 4cm 넓게 잡아 테두리 여백(`half*2 - 0.08`)까지 덮는다.
     */
    {
      /**
       * **뒤판을 안쪽에 두면 창이 봉해진다.** −0.09(실내 쪽)로 뒀더니 안에서
       * 광선을 쏠 때 나오는 것이 전부 이 판이었다 — `window_slider` 조각의
       * 창틀·유리(#8fa6bb)가 뒤에 가려 하나도 안 보이고, 창이 벽에 칠한 남색
       * 사각형이 됐다(2026-09-06 Chris). 이 판의 목적은 **밖에서 볼 때** 유리
       * 뒤에 뭔가 있게 하고 살대 틈으로 실내 불빛이 새지 않게 하는 것이다.
       * 그러니 바깥쪽(+sgn)에 둔다 — 실내에서는 창틀·유리가 먼저 온다.
       */
      const backPlate = sgn * 0.055;
      const [px, pz] = win.along === 'x' ? [half * 2, 0.02] : [0.02, half * 2];
      w.box(
        'glass', 'pane-plate',
        px, y1 - y0, pz,
        win.x + (win.along === 'x' ? 0 : backPlate),
        (y0 + y1) / 2,
        win.z + (win.along === 'x' ? backPlate : 0),
      );
    }

    /**
     * **실내 쪽 창틀과 창턱.** 밤에 안에서 본 창이 어두운 것은 맞지만,
     * 창틀 단차와 창턱이 없으면 벽에 칠한 사각형이다. 벽 안쪽 면에 테를 두르고
     * 아래에 턱을 얹는다 — 이 단차 하나가 「구멍이 뚫려 있다」를 만든다.
     */
    {
      const inF = -sgn * (WALL_T / 2 - 0.012);   // 벽 안쪽 면보다 살짝 안으로
      const rw = 0.055, rt = 0.05;
      const put = (aw: number, ah: number, ax: number, ay: number) => {
        const [sx, sz] = win.along === 'x' ? [aw, rt] : [rt, aw];
        w.box(
          'timber', 'win-reveal', sx, ah, sz,
          win.x + (win.along === 'x' ? ax : inF),
          ay,
          win.z + (win.along === 'x' ? inF : ax),
        );
      };
      const W2 = half * 2;
      put(W2 + rw * 2, rw, 0, y1 + rw / 2);              // 상인방
      put(rw, y1 - y0 + rw * 2, -(half + rw / 2), (y0 + y1) / 2);   // 좌
      put(rw, y1 - y0 + rw * 2, half + rw / 2, (y0 + y1) / 2);      // 우
      // 창턱 — 안으로 더 나온다. 물건을 올려놓는 자리라 두껍고 넓다
      const [tx, tz] = win.along === 'x' ? [W2 + rw * 2, 0.14] : [0.14, W2 + rw * 2];
      w.box(
        'timber', 'win-sill', tx, 0.045, tz,
        win.x + (win.along === 'x' ? 0 : -sgn * (WALL_T / 2 - 0.045)),
        y0 - 0.022,
        win.z + (win.along === 'x' ? -sgn * (WALL_T / 2 - 0.045) : 0),
      );
    }

    for (const [yb, hh] of [
      [y0 + 0.04, hLow],
      [y0 + 0.04 + hLow + GAP, hUp],
    ] as const) {
      for (let c = 0; c < COLS; c++) {
        const off = -W / 2 + cw / 2 + c * (cw + GAP);
        /**
         * 유리 **안쪽으로** 6cm. 벽 중심에 두면 조각 유리(벽면에서 바깥으로
         * 2.1cm)와 3cm 밖에 안 떨어져, 벽이 어느 쪽을 보느냐에 따라 앞뒤가
         * 뒤집힌다. 안쪽 방향은 `-sgn` 이다.
         */
        // 뒤판과 같은 이유로 바깥쪽. 유리(벽면 밖 2.1cm)보다 더 밖이면 유리가
        // 앞에 서서 「유리 뒤의 칸」으로 읽힌다
        const back = sgn * 0.032;
        const [sx, sz] = win.along === 'x' ? [cw, 0.02] : [0.02, cw];
        w.box(
          mat, 'pane-back',
          sx, hh, sz,
          win.x + (win.along === 'x' ? off : back),
          yb + hh / 2,
          win.z + (win.along === 'x' ? back : off),
        );
      }
    }
  }
}

/**
 * 설비 — 실외기 · 계량기함 · 배관.
 *
 * **실외기 하나가 나머지 전부보다 세다.** 시멘트블록 벽에 회색 상자가 하나
 * 걸려 있으면 그 집은 사람이 사는 지금 집이 된다. 폐허와 살림집을 가르는 것이
 * 지붕도 창도 아니고 이것이다.
 *
 * 창고(shed)에는 안 단다. 냉난방할 물건이 없는 곳이다.
 */
function services(w: SlotWriter, plan: FacadePlan, b: BuildingDef) {
  if (plan.archetype === 'shed') return;
  const e = b.entrances[0];
  if (!e) return;

  // 현관과 같은 면에 붙는다 — 그 면이 x 면일 수도 있다(배정호 집). entrance() 와 같은 규약
  const onX = e.along === 'z';
  const outward = onX
    ? (e.x <= (b.x1 + b.x2) / 2 ? -1 : 1)
    : (e.z <= (b.z1 + b.z2) / 2 ? -1 : 1);
  const at = (u: number, y: number, v: number): [number, number, number] =>
    onX ? [e.x + outward * v, y, e.z + u] : [e.x + u, y, e.z + outward * v];
  const size = (along: number, h: number, out: number): [number, number, number] =>
    onX ? [out, h, along] : [along, h, out];

  // 현관에서 옆으로 비켜 단다. 문 위에 달면 드나들 때 머리가 닿는다
  const lo = onX ? Math.min(b.z1, b.z2) : Math.min(b.x1, b.x2);
  const hi = onX ? Math.max(b.z1, b.z2) : Math.max(b.x1, b.x2);
  const along0 = onX ? e.z : e.x;
  const side = hi - along0 > along0 - lo ? 1 : -1;
  const hu = side * 2.6;

  // 실외기. 벽걸이 브래킷 위에 얹는다 — 바닥에 놓으면 눈에 묻힌다
  const hy = 0.92;
  w.box('hvac', 'hvac', ...size(0.86, 0.7, 0.34), ...at(hu, hy, 0.33));
  // 브래킷 다리 둘
  for (const s of [-1, 1]) {
    w.box('grille', 'hvac-bracket', ...size(0.05, 0.26, 0.36), ...at(hu + s * 0.36, hy - 0.46, 0.33));
  }
  // 팬 그릴 — 바깥을 본다. 이 어두운 원판이 「실외기」를 확정한다
  w.box('grille', 'hvac-fan', ...size(0.52, 0.52, 0.03), ...at(hu, hy, 0.51));

  // 배관. 실외기에서 처마까지 올라가는 흰 선. 벽에 세로선이 하나 그어지면
  // 그것만으로 「설비가 지나간다」가 읽힌다
  w.box('pipe', 'hvac-pipe', ...size(0.07, WALL_H - hy - 0.5, 0.07), ...at(hu + 0.3, (WALL_H - 0.5 + hy) / 2, 0.22));

  // 계량기함 — 실외기 반대쪽 끝에 단다.
  // **문패 자리를 비켜야 한다.** e.x - side*1.5 였는데, 문패가 e.x + 1.62 라서
  // 두 집은 12cm 차이로 겹쳤고 계량기함이 6cm 더 앞이라 이름을 덮었다 —
  // 「문패 가로막음」이 그것이다. 실외기를 마주 보는 쪽으로 보낸다
  w.box('hvac', 'meter-box', ...size(0.36, 0.46, 0.15), ...at(-side * 2.6, 1.52, 0.24));

  // 홈통 — 처마 끝에서 땅까지. 폐교에는 원래 있었을 것이고 살림집은 새로 달았다
  const inset = plan.archetype === 'school' ? 0.5 : 0.35;
  w.box('pipe', 'downspout', ...size(0.1, WALL_H - 0.1, 0.1), ...at(lo - along0 + inset, (WALL_H - 0.1) / 2, 0.23));
}

/** 현관 — 캐노피, 계단, 문패, 그리고 현관등. 등이 없으면 문패를 못 읽는다 */
function entrance(w: SlotWriter, root: THREE.Group, plan: FacadePlan, b: BuildingDef) {
  for (const e of b.entrances) {
    // **문이 어느 벽에 났는지 먼저 정한다.** 여기가 z 면만 가정하고 있었다 —
    // 배정호 집 현관은 서쪽 벽(`along:'z'`, x=88)에 나 있는데 캐노피·계단·문패가
    // 전부 남쪽 벽 바깥 허공에 붙어서, 정작 문 앞에는 아무것도 없었다.
    // `along:'z'` 는 벽이 z 를 따라 뻗었다는 뜻이고 곧 **x 면**이다.
    const onX = e.along === 'z';
    const outward = onX
      ? (e.x <= (b.x1 + b.x2) / 2 ? -1 : 1)
      : (e.z <= (b.z1 + b.z2) / 2 ? -1 : 1);

    // 벽면 위의 좌표계. u = 벽을 따라, v = 바깥으로. 이 둘만 쓰면 어느 면이든 같다
    const at = (u: number, y: number, v: number): [number, number, number] =>
      onX ? [e.x + outward * v, y, e.z + u] : [e.x + u, y, e.z + outward * v];
    // 상자 치수도 같이 돌아야 한다. 안 돌리면 캐노피가 벽을 뚫고 옆으로 눕는다
    const size = (along: number, h: number, out: number): [number, number, number] =>
      onX ? [out, h, along] : [along, h, out];

    const school = plan.archetype === 'school';
    const cw = school ? 3.4 : 1.9;
    const cd = school ? 2.0 : 0.9;
    const cy = school ? 2.85 : 2.42;

    w.box('fascia', 'canopy', ...size(cw, 0.16, cd), ...at(0, cy, cd / 2));
    w.box('snow', 'canopy-snow', ...size(cw - 0.1, 0.08, cd - 0.1), ...at(0, cy + 0.12, cd / 2));
    if (school) {
      for (const s of [-1, 1]) {
        w.box('concrete', 'canopy-post', ...size(0.2, cy - 0.1, 0.2), ...at(s * (cw / 2 - 0.22), (cy - 0.1) / 2, cd - 0.24));
      }
    }

    // 계단 — 기단이 있는 만큼 딛고 올라간다.
    //
    // **실치수로 놓는다.** 이 계단은 장식이 아니라 단서가 서는 자리다.
    // 명주원이 새벽 두 시에 마주친 사람이 「키가 좀 작아 보였다」인데
    // (한서진 168 · 송이경 163), 평지에서 5cm 는 아무도 못 알아본다.
    // 계단은 **둘이 서로 다른 높이에 서서 스치는 자리**라 눈높이 차이가
    // 유난히 의식된다. 한 단 올라선 사람과 아래 선 사람의 눈높이 차가
    // 15cm 이므로, 키 5cm 는 그 격자 안에서 읽힌다.
    //
    //   챌판(단 높이)  0.15m   — 실제 규격 150~180mm
    //   디딤판(단 깊이) 0.30m   — 실제 규격 260~300mm
    if (school) {
      const rise = PLINTH.school;
      const RISER = 0.15;
      const TREAD = 0.3;
      const steps = Math.max(1, Math.round(rise / RISER));
      for (let i = 0; i < steps; i++) {
        const h = (rise / steps) * (i + 1);
        w.box('concrete', 'step', ...size(cw * 0.7, h, TREAD), ...at(0, h / 2, 0.2 + TREAD * (steps - i)));
      }
    } else {
      /**
       * 살림집은 **계단을 놓지 않는다.** 기단 45cm 를 딛고 오르는 세 단을 놓아
       * 뒀는데, 정작 문지방과 실내 바닥은 마당과 같은 높이(`baseY`)다 — 올라갈
       * 데가 없는 계단이라 플레이어가 그냥 통과해 걸었고, 툇마루와 함께 문 밑을
       * 가려 「문이 바닥에 잠긴」 그림을 만들었다.
       *
       * 대신 댓돌 한 장. 신 벗는 자리를 표시하는 낮은 판석이라 높이가 필요 없고
       * (9cm — 발이 묻히지 않는다), 눈 쌓인 마당에 가로선이 하나 생겨 현관 앞이
       * 「닦아 놓은 자리」로 읽힌다.
       */
      w.box('granite', 'door-stone', ...size(1.4, 0.09, 0.7), ...at(0, 0.045, 0.62));
    }

    if (!e.label) continue;

    // 문패.
    //
    // 살림집은 문짝 **바깥** 벽에 단다 — 문 폭 반이 0.95m 라 그 안에 두면 물린다.
    // 학교는 다르다. 여기가 벽면(`e.z + outward*0.22`) 에 붙어 있었는데
    // **돌출 현관동이 그 앞 2.6m 를 통째로 채우고 있어서 문패가 인방 속에
    // 파묻혀 있었다** — 마을에서 제일 큰 건물에 이름이 없었던 게 이것이다.
    // 학교 문패는 현관동 **바깥 면**, 개구부 인방 위에 건다.
    const pw = school ? 1.7 : 0.9;
    const gap = DOOR_HALF + pw / 2 + 0.22;
    const PORCH_D = 2.6;                    // schoolPorch 의 depth 와 같아야 한다
    /**
     * **벽 밖으로 확실히 내민다.** 살림집 문패가 벽면에서 6cm 였는데
     * `WALL_T` 가 0.3 이라 벽은 중심선 ±0.15 를 차지한다 — 문패 다섯 장이
     * 통째로 벽 **속에** 박혀 있었다. 다섯 집 전부 이름이 없었던 게 이것이다.
     * 계량기함(0.24)이 보이는 걸 눈금 삼아 그보다 조금 더 내민다.
     */
    const OUT_V = 0.26;
    const plate = new THREE.Mesh(
      new THREE.PlaneGeometry(pw, school ? 0.42 : 0.24),
      new THREE.MeshBasicMaterial({ map: plateTexture(e.label), toneMapped: false }),
    );
    // root 에 직접 붙는 것은 SlotWriter 의 baseY 를 안 거친다. 여기서 더한다
    plate.position.set(...at(school ? 0 : gap, (school ? 2.86 : 1.68) + w.baseY, school ? PORCH_D + 0.05 : OUT_V));
    plate.rotation.y = onX ? outward * (Math.PI / 2) : (outward > 0 ? 0 : Math.PI);
    root.add(plate);

    // 현관등 — 문패를 읽을 수 있을 만큼만. 문패 반대쪽에 단다.
    //
    // **어두운 집은 현관등도 어둡다.** 창만 꺼 놓고 현관등을 그대로 뒀더니
    // 배정호 집이 여전히 마을에서 제일 밝았다 — 등 하나가 판벽 12m 를
    // 통째로 띄운다. 브리핑은 「집은 마을 어느 집보다 어둡다」다.
    const dark = b.style?.lit === 'dark';
    const bulb = new THREE.Mesh(
      new THREE.BoxGeometry(0.16, 0.1, 0.1),
      new THREE.MeshBasicMaterial({ color: dark ? '#6b5a44' : '#ffd9a0' }),
    );
    // 학교는 현관동 바깥 면에 달아야 문패를 비춘다.
    //
    // **살림집 등은 캐노피 밑으로 들여 단다.** 벽면(v=0.24)에 문패 반대쪽으로 붙여
    // 놨더니 등과 문짝이 거의 같은 평면에 놓여서, 빛이 문을 **스치기만** 했다
    // (입사각 여현 0.15). 벽은 하얗게 타는데 문짝은 새까만 구멍이었다 —
    // 문짝을 굽고 나서도 그대로였던 게 이것이다. 캐노피는 이미 0.9m 나와 있다.
    const lampV = school ? PORCH_D + 0.08 : cd * 0.55;
    const lampU = school ? -1.9 : -0.72;
    bulb.position.set(...at(lampU, (school ? cy - 0.28 : cy - 0.3) + w.baseY, lampV));
    root.add(bulb);
    /**
     * 감쇠를 1.6 에서 낮춘다. 이 씬은 간접광이 없어서(`scene.environment` 를
     * 일부러 안 건다) 물리 감쇠를 그대로 쓰면 등에서 2m 떨어진 문 밑동이
     * 0 으로 뭉갠다. 실제로 현관마다 아래 절반이 검은 판이었다.
     */
    const lamp = markDecor(new THREE.PointLight('#ffcf97', dark ? 3.2 : 9, dark ? 5 : 8, 1.2));   // 가까운 몇 개만 켜진다(lightPool.ts)
    lamp.position.copy(bulb.position);
    root.add(lamp);

    /**
     * 학교는 **현관동 안쪽에 등을 하나 더** 단다.
     * 위의 등은 현관동 바깥 면에 붙어 문패를 비춘다. 문은 그보다 2.6m 안쪽에
     * 있어서 빛이 안 닿고, 개구부가 통째로 새까만 구멍으로 나왔다 —
     * 문짝을 굽고 나서도 그대로였다. 인방 밑에 알전구 하나를 매단다.
     */
    if (school) {
      const inBulb = new THREE.Mesh(
        new THREE.SphereGeometry(0.075, 8, 6),
        new THREE.MeshBasicMaterial({ color: '#ffe6b8' }),
      );
      inBulb.position.set(...at(0, 2.16 + w.baseY, 1.05));
      root.add(inBulb);
      const inLamp = markDecor(new THREE.PointLight('#ffd6a2', 5.5, 6, 1.15));
      inLamp.position.copy(inBulb.position);
      root.add(inLamp);
    }
  }
}

/**
 * 학교의 돌출 현관. 한국 학교는 가운데 현관동이 앞으로 튀어나온다 —
 * 일자 막대에 캐노피만 붙이면 창고와 실루엣이 같다.
 */
function schoolPorch(w: SlotWriter, b: BuildingDef) {
  const e = b.entrances[0];
  const outward = e.z <= (b.z1 + b.z2) / 2 ? -1 : 1;
  const depth = 2.6;
  const width = 5.2;
  const cz = e.z + (outward * depth) / 2;
  const H = WALL_H + 0.5;

  // **개구부를 실제로 판다.**
  // 여기가 통짜 상자였다. 주석에는 「개구부는 파고」라고 적어 두고 몸통 박스를
  // 통째로 세워서, 학교 현관 앞을 콘크리트가 막고 있었다 —
  // 「학교는 앞에 막혀있음」이 그것이다. 양옆 벽과 인방으로 다시 세운다.
  const OPEN = 2.8;                       // 문 폭 1.9 보다 넉넉하게
  const side = (width - OPEN) / 2;
  const HEAD = 2.35;                      // 개구부 높이

  for (const s of [-1, 1]) {
    w.box('concrete', 'porch-side', side, H, depth, e.x + s * (OPEN + side) / 2, H / 2, cz);
  }
  // 개구부 위 인방
  w.box('concrete', 'porch-lintel', OPEN, H - HEAD, depth, e.x, HEAD + (H - HEAD) / 2, cz);

  w.box('fascia', 'porch-eave', width + 0.7, 0.24, depth + 0.7, e.x, H + 0.12, cz);
  w.box('snow', 'porch-snow', width + 0.5, 0.12, depth + 0.5, e.x, H + 0.3, cz);

  // 개구부 양옆 벽기둥 — 이제 개구부 밖으로 물러나 있어야 한다
  for (const s of [-1, 1]) {
    w.box('granite', 'porch-pier', 0.34, HEAD, 0.34, e.x + s * (OPEN / 2 + 0.17), HEAD / 2, e.z + outward * (depth - 0.25));
  }
}

/**
 * 지붕 물매. 슬레이트 지붕은 4/10~5/10 이 흔했다 — 26° 가 그 언저리다.
 *
 * 처음에 19° 로 뒀더니 **길에서 지붕이 안 보였다.** 눈높이 3.4m 에서 13m 떨어져
 * 처마를 보면 시선이 14.8° 로 올라가는데, 물매가 그보다 완만하면 눈 얹힌 윗면이
 * 시야에 절대 안 들어온다 — 어두운 띠 한 줄로 끝난다. 물매는 **아래에서 올려다보는
 * 각보다 커야** 지붕으로 읽힌다.
 * 더 세우면 도시 주택이 되고, 더 눕히면 다시 상자가 된다.
 *
 * **이제 집마다 다르다**(`HouseStyle.pitchDeg`). 위 하한선은 그대로 유효하다 —
 * 배정호 집만 19° 로 눕혀 뒀는데, 그건 그 집이 외길 끝에서 **평평하게 보여야**
 * 한다는 의도이고 그래서 거기만 지붕이 어두운 띠로 남는다.
 */
/** 스타일이 없는 집(다른 에피소드)은 지금까지 나오던 그 26° 슬레이트 집 그대로다 */
const DEFAULT_STYLE: HouseStyle = {
  roof: 'slate', pitchDeg: 26, stack: 'brickR', wall: 'block',
};
const styleOf = (b: BuildingDef): HouseStyle => b.style ?? DEFAULT_STYLE;

const ROOF_SLOT = {
  slate: 'roof', tinGreen: 'roofTinGreen', tinBlue: 'roofTinBlue',
  tinRed: 'roofTinRed', tile: 'roofTile',
} as const;

/**
 * 박공지붕. **집을 상자에서 꺼내는 조각이다.**
 *
 * 벽만 세우고 처마 갈매기판으로 끝냈더니 언덕 위 집이 하늘을 자로 그은 듯
 * 평평하게 잘렸다. 브리핑이 요구한 「지붕선이 능선보다 위로 떨어진다」가
 * 성립하지 않는다 — 지붕선 자체가 없었다.
 *
 * 덤으로 이게 이 마을에서 **가장 큰 위를 향한 면**이다. 눈이 쌓일 자리가
 * 지금까지 땅밖에 없었으므로, 지붕이 서는 순간 집이 눈 오는 밤에 놓인다.
 *
 * 용마루는 긴 축을 따른다. 한서진 집은 14×14 정사각이라 x 축이 되고, 그래서
 * 언덕을 올라오는 카메라가 **긴 물매면을 정면으로** 본다.
 */
/**
 * **집만 지붕이 있었다.** `buildFacades` 가 `archetype === 'house'` 일 때만
 * 이걸 불러서, 조합센터는 평지붕 회색 판이 되고 별동은 아예 위가 뚫린 상자로
 * 섰다. 원형별로 처마와 박공 마감만 갈아 끼우면 그대로 쓸 수 있다.
 */
const GABLE_SLOT = { block: 'block', render: 'render', panel: 'siding' } as const;

function gableRoof(w: SlotWriter, b: BuildingDef, arch: Archetype = 'house') {
  const st = styleOf(b);
  const PITCH = (st.pitchDeg * Math.PI) / 180;
  const RS = ROOF_SLOT[st.roof];
  const x1 = Math.min(b.x1, b.x2), x2 = Math.max(b.x1, b.x2);
  const z1 = Math.min(b.z1, b.z2), z2 = Math.max(b.z1, b.z2);
  const cx = (x1 + x2) / 2, cz = (z1 + z2) / 2;
  const alongX = x2 - x1 >= z2 - z1;
  const ridgeLen = alongX ? x2 - x1 : z2 - z1;   // 용마루 방향 길이
  const span = alongX ? z2 - z1 : x2 - x1;       // 물매 방향(벽에서 벽까지)
  const ov = EAVE[arch] + 0.25;                  // 처마 내밈
  const rise = (span / 2) * Math.tan(PITCH);
  /**
   * 처마선을 벽 위 34cm 에 둔다. 벽 바로 위(+0.03)에 얹었더니 이미 있던
   * 갈매기판(`fascia`, 윗변 3.25)과 서까래 끝이 **지붕판을 뚫고 올라와**
   * 물매면에 가로줄이 그어졌다. 지붕은 그것들보다 위에 있어야 한다.
   */
  const eaveY = WALL_H + 0.34;
  const drop = ov * Math.tan(PITCH);             // 내민 처마가 벽선보다 내려가는 양
  const run = span / 2 + ov;
  const slabLen = Math.hypot(run, rise + drop);
  const th = 0.16;

  for (const s of [-1, 1]) {
    // 물매 중점은 용마루(0, ridgeY)와 처마끝(run, eaveY-drop)의 한가운데다
    const mid = (eaveY + rise + eaveY - drop) / 2;
    if (alongX) {
      // +z 쪽 물매는 +z 끝이 내려가야 한다 → rotateX(+PITCH)
      w.tilt(RS, 'roof-slope', ridgeLen + 2 * ov, th, slabLen,
        cx, mid, cz + (s * run) / 2, 'x', s * PITCH);
    } else {
      // rotateZ(+θ) 는 +x 끝을 올린다 → 내리려면 부호를 뒤집는다
      w.tilt(RS, 'roof-slope', slabLen, th, ridgeLen + 2 * ov,
        cx + (s * run) / 2, mid, cz, 'z', -s * PITCH);
    }
  }

  /**
   * **함석 지붕은 골이 전부다.**
   *
   * 물매를 매끈한 판 두 장으로 두면 24m 짜리 지붕이 통째로 회색 사다리꼴
   * 하나가 된다 — 별동이 「컨테이너」로 보인 절반이 이것이었다. 실제 함석은
   * 거멀접기 이음매가 60cm 안팎마다 솟아 있고, 그 선이 빛을 받아 물매를 따라
   * 흐르는 세로 줄무늬를 만든다. 줄무늬 하나로 판때기가 지붕이 된다.
   *
   * 기와·슬레이트는 자기 결이 이미 있으므로 **함석 지붕에만** 얹는다.
   */
  const isTin = st.roof === 'tinGreen' || st.roof === 'tinBlue' || st.roof === 'tinRed';
  if (isTin) {
    const full = ridgeLen + 2 * ov;
    /**
     * **이음매를 크게 만들면 지붕이 아니라 울타리가 된다.**
     *
     * 7cm 폭 x 8cm 높이로 62cm 마다 세워 놨더니, 별동 지붕을 낮은 각도로 보는
     * 컷에서 `atan(0.08/0.62)=7.4도` 아래로는 **살이 지붕판을 통째로 가렸다.**
     * 남는 건 살의 옆면인데 그건 거의 수직이라 반구광의 하늘색을 못 받고
     * 달·채움광 셋 다 등지고 있어서 정확히 `rgb(0,0,0)` 이다. 그래서 지붕이
     * 「검은 판에 흰 점이 박힌 것」으로 찍혔다 (`c1_별동지붕.png` 에서 직접
     * 픽셀을 재서 확인했다 — 진짜 0 이었다).
     *
     * 실제 거멀접기는 **높이 25mm 안팎**이다. 8cm 는 네 배 과장이었다.
     * 낮추면 가리는 각이 2.7도로 떨어져 어떤 컷에서도 판이 보이고, 살은
     * 제 역할인 「60cm 마다 지나가는 가는 선」으로 돌아간다.
     */
    const SEAM_W = 0.03, SEAM_H = 0.028;
    const n = Math.max(2, Math.round(full / 0.56));
    const step = full / n;
    const off = (th + SEAM_H) / 2;               // 지붕판 윗면 위로 솟는 양
    const mid = (eaveY + rise + eaveY - drop) / 2;
    for (const s of [-1, 1]) {
      const rad = alongX ? s * PITCH : -s * PITCH;
      // 기울인 판의 윗면 법선. 이 방향으로 밀어야 이음매가 판 **위에** 얹힌다 —
      // 안 밀면 판 두께 안에 파묻혀 한 줄도 안 보인다
      const nx = alongX ? 0 : -Math.sin(rad);
      const ny = Math.cos(rad);
      const nz = alongX ? Math.sin(rad) : 0;
      for (let i = 0; i <= n; i++) {
        const t = -full / 2 + i * step;
        if (alongX) {
          w.tilt(RS, 'roof-seam', SEAM_W, SEAM_H, slabLen,
            cx + t, mid + ny * off, cz + (s * run) / 2 + nz * off, 'x', rad);
        } else {
          w.tilt(RS, 'roof-seam', slabLen, SEAM_H, SEAM_W,
            cx + (s * run) / 2 + nx * off, mid + ny * off, cz + t, 'z', rad);
        }
      }
    }
  }

  /**
   * 처마 물받이. 흰 PVC 홈통은 어두운 지붕과 벽 사이를 가르는 **가로선 한 줄**
   * 이다. 시골 작업동 사진에 거의 빠짐없이 달려 있고, 없으면 지붕이 벽에 그냥
   * 얹힌 판으로 보인다. 밤 컷에서는 이 흰 선이 처마의 유일한 윤곽이 된다.
   */
  for (const s of [-1, 1]) {
    const ge = span / 2 + ov;
    const gy = eaveY - drop - 0.07;
    if (alongX) w.box('pipe', 'roof-gutter', ridgeLen + 2 * ov, 0.13, 0.15, cx, gy, cz + s * ge);
    else w.box('pipe', 'roof-gutter', 0.15, 0.13, ridgeLen + 2 * ov, cx + s * ge, gy, cz);
  }

  // 용마루 마루기와. 두 물매가 만나는 선을 덮어 틈과 지저분한 모서리를 지운다
  if (alongX) w.box(RS, 'roof-ridge', ridgeLen + 2 * ov, 0.18, 0.36, cx, eaveY + rise + 0.05, cz);
  else w.box(RS, 'roof-ridge', 0.36, 0.18, ridgeLen + 2 * ov, cx, eaveY + rise + 0.05, cz);

  // 박공 끝 삼각벽. 시멘트블록 그대로 올라간다 — 처마 밑이 뚫려 보이면 안 된다.
  // **벽 윗변에서 시작한다**(처마선이 아니라) — 그 사이 34cm 가 비면 박공 쪽에서
  // 지붕 밑이 뚫려 보인다. 그만큼 높여 두면 삼각형이 물매면보다 조금 가팔라지지만,
  // 용마루에서 정확히 만나고 그 아래에서는 항상 지붕판 밑에 있으므로 안 튀어나온다
  /**
   * 굴뚝. 레퍼런스 사진 다섯 장 중 셋에 굴뚝이 있다 — 눈 덮인 지붕은 면이
   * 통째로 하얘서 **실루엣을 끊어 줄 수직 요소**가 없으면 흰 삼각형 한 장이 된다.
   * 용마루 한가운데가 아니라 3할쯤 치우쳐 세운다(가운데면 대칭이라 장식이 된다).
   * 갓은 `roof` 슬롯이라 위를 향한 면에 눈이 얹히고, 벽돌 몸통은 수직이라 안 얹힌다.
   */
  if (st.stack !== 'none') {
    // **좌/우가 갈려야 한다.** 굴뚝을 전부 같은 쪽에 세워 뒀더니 좌우 대칭인
    // 두 집(차유리·명주원)이 컷에서 서로의 거울상이 됐다. 부호 하나가
    // 「같은 집을 반대편에서 본 것」과 「다른 집」을 가른다.
    const side = st.stack === 'brickL' ? -1 : 1;
    const ax = cx + (alongX ? ridgeLen * 0.28 * side : 0);
    const az = cz + (alongX ? 0 : ridgeLen * 0.28 * side);
    if (st.stack === 'flue') {
      /**
       * 양철 연통. 벽돌 굴뚝을 못 놓은 집(연탄난로·기름보일러)은 이걸 낸다 —
       * 굵기가 4분의 1이라 실루엣에서 **선 하나**로 남는다. 벽돌 굴뚝이
       * 「덩어리」라면 이건 「획」이고, 그 차이가 60m 밖에서도 보인다.
       * 용마루가 아니라 물매 중턱을 뚫고 나오게 둔다.
       */
      // **현관이 있는 쪽 물매로 낸다.** 반대편에 세우면 다가오는 길에서 지붕에
      // 가려 안 보인다 — 긴 상자에서 실루엣을 끊어 줄 유일한 수직 요소가 그렇게 죽었다.
      const ent = b.entrances?.[0];
      const near = ent ? Math.sign((alongX ? ent.z - cz : ent.x - cx)) || 1 : 1;
      const px = cx + (alongX ? ridgeLen * 0.3 : run * 0.55 * near);
      const pz = cz + (alongX ? run * 0.55 * near : ridgeLen * 0.3);
      const roofY = eaveY + rise - (run * 0.55 / run) * rise;
      const top = roofY + 1.9;
      w.box('hvac', 'roof-flue', 0.22, top - (WALL_H - 0.4), 0.22, px, (top + WALL_H - 0.4) / 2, pz);
      w.box('grille', 'roof-flue-cap', 0.36, 0.1, 0.36, px, top + 0.05, pz);
    } else {
      const top = eaveY + rise + 1.05;
      // **천장 위에서 시작한다.** WALL_H − 0.6 으로 두었더니 방을 나누고 나서 업무공간
      // 천장 밑에 벽돌 상자가 매달려 보였다(한서진 집 H3 컷). 처마 위 물매 밑은 어차피
      // 지붕이 가리므로 벽 꼭대기에서 시작해도 밖에서는 같다
      const base = WALL_H + 0.02;
      w.box('brick', 'roof-chimney', 0.72, top - base, 0.72, ax, (top + base) / 2, az);
      w.box(RS, 'roof-chimney-cap', 0.94, 0.13, 0.94, ax, top + 0.065, az);
    }
  }

  const gt = 0.3;
  const GS = GABLE_SLOT[st.wall];
  const gBase = WALL_H - 0.05;
  for (const s of [-1, 1]) {
    if (alongX) {
      const gx = s < 0 ? x1 + gt / 2 : x2 - gt / 2;
      w.gable(GS, 'roof-gable', span / 2, eaveY + rise - gBase, gt, gx, gBase, cz, Math.PI / 2);
    } else {
      const gz = s < 0 ? z1 + gt / 2 : z2 - gt / 2;
      w.gable(GS, 'roof-gable', span / 2, eaveY + rise - gBase, gt, cx, gBase, gz, 0);
    }
  }
}

/**
 * 부감에서 걷어내야 하는 지붕 메시들. `buildFacades` 가 채운다 —
 * 지붕을 세운 뒤 부감을 켜면 마을이 통째로 지붕 등판만 보인다.
 */
export const facadeRoofs: THREE.Object3D[] = [];

export function buildFacades(scene: THREE.Scene): FacadeDiagnostics {
  const root = new THREE.Group();
  scene.add(root);
  const mats = slots();
  const w = new SlotWriter();
  const plans = planFacades();

  let bays = 0;
  let skipped = 0;
  for (const plan of plans) {
    const b = BUILDINGS.find((x) => x.id === plan.id)!;
    w.baseY = b.baseY ?? 0;
    for (const e of plan.edges) {
      if (!e.exposed) {
        skipped++;
        continue;
      }
      bays += e.bays.length;
      cladEdge(w, plan, b, e);
    }
    w.baseY = b.baseY ?? 0;
    if (plan.archetype === 'school') schoolPorch(w, b);
    gableRoof(w, b, plan.archetype);
    entrance(w, root, plan, b);
    services(w, plan, b);
    w.baseY = 0;
  }
  // 창틀은 건물별이 아니라 개구부별이라 바깥 루프에서 한 번만 돈다
  sashes(w);

  // 창빛 자국 — 전부 한 메시로 굽는다(draw call 1)
  {
    const spill = new LightPools();
    for (const p of litSpill) {
      spill.rect(p.x, p.y, p.z, p.rx, p.rz, p.dim ? '#e0a35e' : '#ffca85', p.dim ? 0.16 : 0.3);
    }
    spill.flush(root);
  }

  const triangles = w.flush(root, mats);
  facadeRoofs.length = 0;
  // 슬롯이 `roof`, `roofTinRed` … 다섯으로 갈렸다. 접두사로 잡지 않으면
  // 부감에서 지붕 넷이 안 걷히고 마을이 지붕 등판으로 덮인다
  for (const c of root.children) if (String(c.userData.slot).startsWith('roof')) facadeRoofs.push(c);
  return {
    buildings: plans.length,
    bays,
    hiddenEdgesSkipped: skipped,
    modules: { ...w.counts },
    triangles,
  };
}
